import { createApi } from '@reduxjs/toolkit/query/react';
import { Config } from '@/config/environment';
import { sfAuth } from '@/auth/SFAuth';
import { withRetry } from '@/utils/retryUtils';
import { emitSessionExpired } from '@/auth/sessionEvents';
import {
  buildRequestError,
  parseOperationName,
  truncateBody,
  type GraphQLErrorLike,
  type RequestError,
} from './requestError';

const ENDPOINT_LABEL = 'admin-api';

// --- Types ---

export enum DeleteRequestStatus {
  Pending = 'PENDING',
  Approved = 'APPROVED',
  Cancelled = 'CANCELLED',
  Processing = 'PROCESSING',
  Succeeded = 'SUCCEEDED',
  Failed = 'FAILED',
}

export interface DeleteTenantStreamsRequestDocument {
  id: string;
  tenantId: string;
  module: string;
  createdFrom: string | null;
  createdTo: string | null;
  version: number;
  requestedBy: string;
  requestedAt: string;
  approvedBy: string | null;
  approvedAt: string | null;
  status: DeleteRequestStatus;
  started: string | null;
  ended: string | null;
  streamsDeleted: number;
  mongoCollectionDeletes: string | null;
  checkpointPosition: string | number | null;
  errorMessage: string | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
}

interface ResetTenantRequestPayload {
  request: DeleteTenantStreamsRequestDocument | null;
  errors: { message: string }[] | null;
}

interface DeleteTenantStreamsRequestsConnection {
  nodes: DeleteTenantStreamsRequestDocument[];
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
  totalCount: number;
}

export enum DeleteStreamsStartFrom {
  Beginning = 'BEGINNING',
  End = 'END',
}

export interface DeleteStreamsRequestDocument {
  id: string;
  prefix: string;
  version: number;
  startFrom: DeleteStreamsStartFrom;
  status: DeleteRequestStatus;
  requestedBy: string;
  requestedAt: string;
  approvedBy: string | null;
  approvedAt: string | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
  started: string | null;
  ended: string | null;
  streamsDeleted: number;
  checkpointPosition: string | number | null;
  errorMessage: string | null;
}

interface DeleteStreamsRequestPayload {
  request: DeleteStreamsRequestDocument | null;
  errors: { message: string }[] | null;
}

interface DeleteStreamsRequestsConnection {
  nodes: DeleteStreamsRequestDocument[];
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
  totalCount: number;
}

export interface KeyVaultSecretTag {
  key: string;
  value: string;
}

export interface KeyVaultSecretInfo {
  name: string;
  createdOn: string | null;
  updatedOn: string | null;
  tags: KeyVaultSecretTag[];
}

export interface KeyVaultSecretPayload {
  secret: KeyVaultSecretInfo | null;
  errors: { message: string }[] | null;
}

export interface CreateKeyVaultSecretInput {
  secretName: string;
  secretValue: string;
}

export interface DailyTransactionSummaryDocument {
  id: string;
  tenantId: string;
  date: string;
  customerCode: string;
  locationCode: string;
  itemCode: string;
  itemCost: number;
  unitsSold: number | null;
}

export interface DailyTransactionSummariesEdge {
  cursor: string;
  node: DailyTransactionSummaryDocument;
}

export interface DailyTransactionSummariesPageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface DailyTransactionSummariesConnection {
  edges: DailyTransactionSummariesEdge[];
  pageInfo: DailyTransactionSummariesPageInfo;
  totalCount: number;
}

export interface GetDailyTransactionSummariesArgs {
  first?: number;
  after?: string | null;
  where?: Record<string, unknown> | null;
  order?: Record<string, 'ASC' | 'DESC'>[] | null;
}

export interface EntityCheckpointDocument {
  id: string;
  position: string;
}

interface EntityCheckpointsConnection {
  nodes: EntityCheckpointDocument[];
  totalCount: number;
}

export interface EventStoreDocument {
  id: string;
  lastPosition: number;
  lastUpdated: string;
}

export interface UpdateEntityCheckpointInput {
  id: string;
  position: number;
}

export interface AdminEntityCheckpointPayload {
  checkpoint: EntityCheckpointDocument | null;
  errors: { message: string }[] | null;
}

export interface AvailableTenant {
  id: string;
  name: string;
}

export interface SwitchTenantPayload {
  success: boolean;
  reAuthRequired: boolean;
  selectedTenantId: string | null;
  errors: { message: string }[] | null;
}

export interface FusionAuthApplicationRole {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isSuperRole: boolean;
}

export interface FusionAuthApplication {
  id: string;
  name: string;
  roles: FusionAuthApplicationRole[];
}

export interface FusionAuthRegistration {
  applicationId: string;
  roles: string[];
}

/**
 * Where a FusionAuth user's assigned tenant id resolves from, mirroring the JWT-populate
 * lambda: WSAPI (`data.wsapi.tenantId`, primary), SILVERFERN (`data.silverfern.tenantId`,
 * legacy fallback), or NOT_SET (neither resolves).
 */
export type FusionAuthTenantSource = 'WSAPI' | 'SILVERFERN' | 'NOT_SET';

export interface FusionAuthUser {
  id: string;
  email: string | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  mobilePhone: string | null;
  tenantId: string | null;
  tenantSource: FusionAuthTenantSource;
  /** Whether the FusionAuth login is active (not locked/deactivated). */
  active: boolean;
  /** Whether the FusionAuth login's email has been verified. */
  emailVerified: boolean;
  registrations: FusionAuthRegistration[];
}

export interface SearchFusionAuthUsersInput {
  query?: string | null;
  page?: number | null;
  pageSize?: number | null;
}

export interface FusionAuthUserSearchPayload {
  success: boolean;
  total: number | null;
  users: FusionAuthUser[] | null;
  errors: { message: string }[] | null;
}

export interface GetFusionAuthUserInput {
  id: string;
}

export interface FusionAuthUserPayload {
  success: boolean;
  data: FusionAuthUser | null;
  errors: { message: string }[] | null;
}

export interface SetFusionAuthUserTenantInput {
  userId: string;
  tenantId: string;
}

export interface SetFusionAuthUserTenantPayload {
  success: boolean;
  tenantId: string | null;
  tenantSource: FusionAuthTenantSource | null;
  errors: { message: string }[] | null;
}

export interface SetFusionAuthUserTenantsInput {
  userIds: string[];
  tenantId: string;
}

/**
 * PATCH semantics (mirrors the backend RFC 7386 merge-patch): a null/omitted field is
 * left unchanged; an empty/whitespace string **clears** the field in FusionAuth.
 */
export interface UpdateFusionAuthUserInput {
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  mobilePhone?: string | null;
}

export interface UpdateFusionAuthUserPayload {
  success: boolean;
  user: FusionAuthUser | null;
  errors: { message: string }[] | null;
}

export interface ReactivateFusionAuthUserInput {
  userId: string;
}

export interface ReactivateFusionAuthUserPayload {
  success: boolean;
  user: FusionAuthUser | null;
  errors: { message: string }[] | null;
}

export interface DeleteFusionAuthUserInput {
  userId: string;
}

export interface DeleteFusionAuthUserPayload {
  success: boolean;
  deletedUserId: string | null;
  errors: { message: string }[] | null;
}

export interface DeleteFusionAuthUsersInput {
  userIds: string[];
}

export interface GetFusionAuthApplicationInput {
  id: string;
}

export interface FusionAuthApplicationPayload {
  success: boolean;
  data: FusionAuthApplication | null;
  errors: { message: string }[] | null;
}

export interface RegistrationRoleStatus {
  name: string;
  isOrphan: boolean;
}

export interface FusionAuthApplicationUser {
  id: string;
  email: string | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  roles: RegistrationRoleStatus[];
}

export interface GetFusionAuthApplicationUsersInput {
  applicationId: string;
  role?: string | null;
  page?: number | null;
  pageSize?: number | null;
}

export interface ApplicationUsersPayload {
  success: boolean;
  users: FusionAuthApplicationUser[] | null;
  total: number | null;
  errors: { message: string }[] | null;
}

export interface RegisterApplicationToUsersInput {
  applicationId: string;
  userIds: string[];
  initialRoles?: string[] | null;
}

export interface UnregisterApplicationFromUsersInput {
  applicationId: string;
  userIds: string[];
}

export interface AddApplicationRoleToUsersInput {
  applicationId: string;
  role: string;
  userIds: string[];
}

export interface RemoveApplicationRoleFromUsersInput {
  applicationId: string;
  role: string;
  userIds: string[];
}

export interface SetFusionAuthUserRegistrationRolesInput {
  userId: string;
  applicationId: string;
  roles: string[];
}

export interface SetFusionAuthUserRegistrationRolesPayload {
  success: boolean;
  registration: FusionAuthRegistration | null;
  errors: { message: string }[] | null;
}

export interface BulkRoleResult {
  userId: string;
  ok: boolean;
  errorCode: string | null;
  errorMessage: string | null;
  noopReason: string | null;
}

export interface BulkRoleMutationPayload {
  allSucceeded: boolean;
  results: BulkRoleResult[];
  errors: { message: string }[] | null;
}

export interface FusionAuthGroupSummary {
  id: string;
  name: string;
  memberCount: number;
  roleCount: number;
}

export interface FusionAuthGroupRoleRef {
  id: string;
  name: string;
}

export interface FusionAuthGroupApplicationRoles {
  applicationId: string;
  applicationName: string;
  roles: FusionAuthGroupRoleRef[];
}

export interface FusionAuthGroupMember {
  userId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  missingRegistrationApplicationIds: string[];
}

export interface FusionAuthGroupDetail {
  id: string;
  name: string;
  applicationRoles: FusionAuthGroupApplicationRoles[];
  members: FusionAuthGroupMember[];
}

export interface FusionAuthGroupPayload {
  success: boolean;
  data: FusionAuthGroupDetail | null;
  errors: { message: string }[] | null;
}

export interface GroupMembersInput {
  groupId: string;
  userIds: string[];
}

export interface GroupRolesInput {
  groupId: string;
  roleIds: string[];
}

export interface BulkGroupItemResult {
  itemId: string;
  ok: boolean;
  errorCode: string | null;
  errorMessage: string | null;
  noopReason: string | null;
}

export interface BulkGroupMutationPayload {
  allSucceeded: boolean;
  results: BulkGroupItemResult[];
  errors: { message: string }[] | null;
}

// --- Event Stream Aggregate ---

export interface KeyValuePairOfStringAndInt64 {
  key: string;
  /** GraphQL `Long` — may serialize as string. Coerce with `Number()` when treating as a number. */
  value: string | number;
}

export interface EventStreamAggregateConfigDocument {
  id: string;
  enabled: boolean;
  frequencyDays: number;
  /** LocalTime — 24-hr `HH:mm:ss` */
  executionTime: string;
  timeZone: string;
  trackGreaterThan: number;
  checkpointInterval: number;
  checkpointTimeIntervalMin: number;
  /** Worker-owned. Read-only in the PWA. */
  nextExecutionDate: string | null;
  createdOn: string;
  createdBy: string;
  lastModifiedOn: string;
  lastModifiedBy: string;
}

export interface EventStreamAggregateConfigInput {
  enabled: boolean;
  frequencyDays: number;
  executionTime: string;
  timeZone: string;
  trackGreaterThan: number;
  checkpointInterval: number;
  checkpointTimeIntervalMin: number;
}

export interface EventStreamAggregateSnapshotDocument {
  id: string;
  snapshottedAt: string;
  /** GraphQL `Long` — string in JSON. */
  checkpointPositionAtSnapshot: string | number;
  eventCounts: KeyValuePairOfStringAndInt64[];
  streamCounts: KeyValuePairOfStringAndInt64[];
}

export interface EventStreamAggregateSnapshotsConnection {
  nodes: EventStreamAggregateSnapshotDocument[] | null;
  edges: { cursor: string; node: EventStreamAggregateSnapshotDocument }[] | null;
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
  totalCount: number;
}

export interface GetEventStreamAggregateSnapshotsArgs {
  first?: number;
  after?: string;
  where?: Record<string, unknown>;
  order?: Record<string, unknown>[];
}

// --- Scavenge ---

export enum ScavengeLogStatus {
  Unknown = 'UNKNOWN',
  Running = 'RUNNING',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Stopped = 'STOPPED',
  Error = 'ERROR',
}

export interface ScavengeStatus {
  scavengeId: string;
  scavengeResult: string;
  scavengeCompletedPercent: number;
  spaceReclaimed: string | number;
  chunksScavenged: number;
  error: string | null;
}

export interface ScavengeLogDocument {
  scavengeId: string;
  startTime: string;
  endTime: string | null;
  checkTime: string;
  duration: string;
  status: ScavengeLogStatus;
  scavengeStatus: ScavengeStatus | null;
  threadCount: number;
  errorMessage: string | null;
  stopRequested: string | null;
  stopRequestedBy: string | null;
  stopped: string | null;
}

export interface ScavengeLogsConnection {
  nodes: ScavengeLogDocument[] | null;
  edges: { cursor: string; node: ScavengeLogDocument }[] | null;
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
  totalCount: number;
}

export interface GetScavengeLogsArgs {
  first?: number;
  after?: string;
  where?: Record<string, unknown>;
  order?: Record<string, unknown>[];
}

export interface ScavengeConfigDocument {
  id: string;
  enabled: boolean;
  frequencyDays: number;
  /** LocalTime — 24-hr `HH:mm:ss` */
  executionTime: string;
  timeZone: string;
  threadCount: number;
  createdOn: string;
  createdBy: string;
  lastModifiedOn: string;
  lastModifiedBy: string;
}

export interface ScavengeConfigInput {
  enabled: boolean;
  frequencyDays: number;
  /** LocalTime — 24-hr `HH:mm:ss` */
  executionTime: string;
  timeZone: string;
  threadCount: number;
}

// --- ACR (Azure Container Registry) ---

// Per-tag lock state from the admin-api acrRepositoryTags read-model; `locked` reflects the last scan.
export interface AcrRepositoryTag {
  name: string;
  locked: boolean;
}

// Mirrors the admin-api acrRepositories read-model (relman Mongo); admin-api never calls the ACR SDK.
export interface AcrRepositoryDocument {
  id: string;
  repository: string;
  excludedFromCleanup: boolean;
  tagCount: number;
  lockedTagCount: number;
  lastScannedUtc: string;
  tags: string[] | null;
}

export interface AcrRepositoriesEdge {
  cursor: string;
  node: AcrRepositoryDocument;
}

export interface AcrRepositoriesPageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface AcrRepositoriesConnection {
  edges: AcrRepositoriesEdge[];
  pageInfo: AcrRepositoriesPageInfo;
  totalCount: number;
}

export interface GetAcrRepositoriesArgs {
  first?: number;
  after?: string | null;
  where?: Record<string, unknown> | null;
  order?: Record<string, 'ASC' | 'DESC'>[] | null;
}

// --- ACR Settings (kill switch / schedule / excluded / retention) ---

// Singleton cleanup settings the relman orchestrator reads — admin-api owns the write surface.
// scheduleDays/scheduleTime/scheduleTimeZone are null together when no schedule has been configured
// yet (a valid state — the settings document still loads with the kill switch and no schedule).
export interface AcrCleanupSettingsDocument {
  id: string;
  killSwitchEnabled: boolean;
  scheduleDays: string[] | null;
  scheduleTime: string | null;
  scheduleTimeZone: string | null;
  updatedAtUtc: string;
  updatedBy: string | null;
}

export interface AcrCleanupSettingsPayload {
  settings: AcrCleanupSettingsDocument | null;
  errors: { message: string }[] | null;
}

// Per-environment scan target carrying the tags-to-retain count.
export interface AcrScanTargetDocument {
  id: string;
  environment: string;
  kubeContext: string;
  namespace: string;
  tagsToRetain: number;
  updatedAtUtc: string;
  imagePattern: string | null;
  repositoryPattern: string | null;
}

export interface AcrScanTargetPayload {
  scanTarget: AcrScanTargetDocument | null;
  errors: { message: string }[] | null;
}

export interface AcrRepositoryPayload {
  repository: AcrRepositoryDocument | null;
  errors: { message: string }[] | null;
}

export interface SetAcrKillSwitchInput {
  enabled: boolean;
}

export interface SetAcrCleanupScheduleInput {
  scheduleDays: string[];
  scheduleTime: string;
  scheduleTimeZone: string;
}

export interface SetAcrRepositoryExcludedInput {
  repository: string;
  excluded: boolean;
}

export interface SetAcrRetentionInput {
  environment: string;
  tagsToRetain: number;
  kubeContext?: string | null;
  namespace?: string | null;
}

// --- ACR Audit Log (Log tab) ---

// The action an ACR audit-log entry records. Mirrors the relman/admin-api AcrAuditAction enum
// (string wire format); `Manual` source rows carry one of Locked/Unlocked/Deleted.
export enum AcrAuditAction {
  Locked = 'Locked',
  Unlocked = 'Unlocked',
  Deleted = 'Deleted',
  Skipped = 'Skipped',
  ReportOnly = 'ReportOnly',
}

// Why the retention evaluator reached the recorded action. `Manual` is stamped on operator-initiated
// entries written by the relman manual-command processor.
export enum AcrTagReason {
  PatternProtected = 'PatternProtected',
  InUse = 'InUse',
  GracePeriod = 'GracePeriod',
  RetentionKeep = 'RetentionKeep',
  Unreferenced = 'Unreferenced',
  Manual = 'Manual',
}

// The run mode in effect when the audit entry was written. `Manual` marks operator-initiated entries.
export enum AcrCleanupMode {
  ReportOnly = 'ReportOnly',
  LockOnly = 'LockOnly',
  Full = 'Full',
  Manual = 'Manual',
}

// Mirrors the admin-api acrAuditLog read-model (relman Mongo, camelCase wire); admin-api reads it directly.
export interface AcrAuditLogDocument {
  id: string;
  runId: string | null;
  repository: string;
  tag: string;
  action: AcrAuditAction;
  reason: AcrTagReason;
  mode: AcrCleanupMode;
  timestampUtc: string;
  source: string;
  errorMessage: string | null;
}

export interface AcrAuditLogEdge {
  cursor: string;
  node: AcrAuditLogDocument;
}

export interface AcrAuditLogPageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface AcrAuditLogConnection {
  edges: AcrAuditLogEdge[];
  pageInfo: AcrAuditLogPageInfo;
  totalCount: number;
}

export interface GetAcrAuditLogArgs {
  first?: number;
  after?: string | null;
  where?: Record<string, unknown> | null;
  order?: Record<string, 'ASC' | 'DESC'>[] | null;
}

// --- ACR Action Commands (manual lock / unlock / delete) ---

// The manual ACR mutation an operator requested from the PWA. Mirrors relman/admin-api AcrActionType.
export enum AcrActionType {
  Lock = 'Lock',
  Unlock = 'Unlock',
  Delete = 'Delete',
}

// The processing lifecycle of a manual command; admin-api writes `Pending`, relman drives it terminal.
export enum AcrCommandStatus {
  Pending = 'Pending',
  Processing = 'Processing',
  Succeeded = 'Succeeded',
  Failed = 'Failed',
}

// Mirrors the admin-api acrActionCommands read/write-model (relman Mongo, camelCase wire).
export interface AcrActionCommandDocument {
  id: string;
  repository: string;
  tag: string;
  action: AcrActionType;
  status: AcrCommandStatus;
  requestedBy: string | null;
  requestedAtUtc: string;
  processedAtUtc: string | null;
  error: string | null;
}

export interface AcrActionCommandPayload {
  command: AcrActionCommandDocument | null;
  errors: { message: string }[] | null;
}

export interface AcrActionCommandsEdge {
  cursor: string;
  node: AcrActionCommandDocument;
}

export interface AcrActionCommandsConnection {
  edges: AcrActionCommandsEdge[];
  pageInfo: AcrAuditLogPageInfo;
  totalCount: number;
}

export interface GetAcrActionCommandsArgs {
  first?: number;
  after?: string | null;
  where?: Record<string, unknown> | null;
  order?: Record<string, 'ASC' | 'DESC'>[] | null;
}

// Each manual action shares the same boundary shape: { repository, tag }.
export interface AcrTagActionInput {
  repository: string;
  tag: string;
}

// --- Background Service Scaling ---

export interface SetAdminBackgroundServiceReplicasInput {
  deploymentName: string;
  replicas: number;
  switchStrategyToRollingUpdate?: boolean;
}

export interface BackgroundServiceDeploymentStatus {
  deploymentName: string;
  desiredReplicas: number | null;
  readyReplicas: number | null;
}

export interface BackgroundServiceDeploymentStatusPayload {
  deploymentStatus: BackgroundServiceDeploymentStatus | null;
  errors: { message: string }[] | null;
}

export interface AdminBackgroundServicePodInstance {
  podName: string;
  phase: string;
  ready: boolean;
  restartCount: number;
  ageSeconds: number;
}

export interface AdminBackgroundServicePodStatus {
  deploymentName: string;
  deploymentExists: boolean;
  desiredReplicas: number | null;
  readyReplicas: number | null;
  availableReplicas: number | null;
  totalRestartCount: number;
  oldestPodAgeSeconds: number | null;
  pods: AdminBackgroundServicePodInstance[];
  reason: string | null;
}

export interface AdminBackgroundServicePodStatusesPayload {
  statuses: AdminBackgroundServicePodStatus[] | null;
  errors: { message: string }[] | null;
}

// --- Deploy Dashboard — Repo Deployment State ---

// Live deployment state for one configured repo: the image tag running in the stage
// namespace and the image tag running in the production namespace. A tag is null when
// no deployment for the repo exists in that namespace, or it carries no resolvable image.
export interface RepoDeploymentState {
  repo: string;
  stageImageTag: string | null;
  productionImageTag: string | null;
}

// Query payload carrying one RepoDeploymentState per configured repo, or the errors array
// when the live AKS read failed (states is null in that case).
export interface RepoDeploymentStatePayload {
  states: RepoDeploymentState[] | null;
  errors: { message: string }[] | null;
}

// --- Deploy Dashboard — AKS Namespace Health ---

// The two namespaces the aksNamespaceHealth query is allowed to inspect (server-side whitelist).
// stage == worksuite-stage, production == worksuite-production.
export const AKS_STAGE_NAMESPACE = 'worksuite-stage';
export const AKS_PRODUCTION_NAMESPACE = 'worksuite-production';

// Health of a single pod in the namespace. `ready` is true only when every container reports Ready
// (matching `kubectl get pods`); `reason` carries the first notable not-ready reason (CrashLoopBackOff,
// an image-pull failure, a stuck ContainerCreating, etc.) when the pod is unhealthy, otherwise null.
export interface AksPodHealth {
  podName: string;
  deploymentName: string;
  ready: boolean;
  restartCount: number;
  reason: string | null;
}

// Rollout health of a single deployment, derived the way `kubectl rollout status` reports it.
// `rolloutComplete` is the single boolean the panel reads to flag an in-progress or stuck rollout;
// `reason` explains an incomplete rollout (a pod waiting reason, or the failing deployment condition).
export interface AksDeploymentHealth {
  deploymentName: string;
  desiredReplicas: number | null;
  readyReplicas: number | null;
  updatedReplicas: number | null;
  rolloutComplete: boolean;
  reason: string | null;
}

// Aggregate health for one namespace. `healthy` is true only when every deployment's rollout is
// complete and every pod is ready; the unhealthy/notReady counts let the panel render a banner
// without re-walking the full lists.
export interface AksNamespaceHealth {
  namespace: string;
  healthy: boolean;
  deploymentCount: number;
  unhealthyDeployments: number;
  podCount: number;
  notReadyPods: number;
  deployments: AksDeploymentHealth[];
  pods: AksPodHealth[];
}

// Query payload carrying the namespace health, or the errors array when the live AKS read (or the
// namespace whitelist validation) failed (health is null in that case).
export interface AksNamespaceHealthPayload {
  health: AksNamespaceHealth | null;
  errors: { message: string }[] | null;
}

// --- Deploy Dashboard — Pending Approvals ---

// A single pending production-stage approval gating a pipeline run, read live from Azure DevOps.
// `repo` is the Deploy Dashboard repo key the approval was discovered under; `runId` and `approvalId`
// are the identifiers the approvePipelineRun mutation needs to action it. `requestedBy`/`requestedOn`
// are null when ADO did not record them.
export interface PendingApproval {
  repo: string;
  pipelineDefinitionId: number;
  pipelineName: string;
  runId: string;
  runName: string;
  approvalId: string;
  stage: string;
  requestedBy: string | null;
  requestedOn: string | null;
}

// Query payload carrying the pending approvals across the requested repos (every configured repo, or
// just the filtered one), or the errors array when the live ADO read failed (approvals is null then)
// while the request itself still succeeds.
export interface PendingApprovalsPayload {
  approvals: PendingApproval[] | null;
  errors: { message: string }[] | null;
}

// Identifiers the PWA passes back to approve one pending approval. The multi-select flow fans out to
// one approvePipelineRun call per selected approval, each carrying its own repo/runId/approvalId.
export interface ApprovePipelineRunInput {
  repo: string;
  runId: string;
  approvalId: string;
  comment?: string | null;
}

// Mutation payload for approvePipelineRun. `success` is true when the ADO approve call succeeded; on
// failure it is false and `errors` carries the validation or ADO reason (surfaced, never thrown).
export interface ApprovePipelineRunPayload {
  success: boolean;
  errors: { message: string }[] | null;
}

export interface IotIngestionOpsMetricsRow {
  id: string;
  windowStart: string;
  windowEnd: string;
  processedCount: number;
  dedupeHitCount: number;
  deadLetterCount: number;
  unclaimedDeviceSkipCount: number;
  countAppliedCount: number;
  countUnchangedCount: number;
  countUnattributedCount: number;
  countRejectedCount: number;
  maxLagMs: number;
  avgLagMs: number;
}

export type IotDeadLetterReason =
  | 'MalformedPayload'
  | 'UnknownDevice'
  | 'DrainReplayFailed'
  | 'CountSessionRejected'
  | 'CountUnattributed'
  | 'EnvelopeValidationFailed';

// --- Fernie Conversations (read-only staff view of AI orchestrator chats) ---

export enum FernieTurnOutcome {
  Answered = 'ANSWERED',
  Declined = 'DECLINED',
  DocsNoHit = 'DOCS_NO_HIT',
  DocsSearchFailed = 'DOCS_SEARCH_FAILED',
  ToolError = 'TOOL_ERROR',
  Error = 'ERROR',
  Cancelled = 'CANCELLED',
  GuardrailDenied = 'GUARDRAIL_DENIED',
  RateLimited = 'RATE_LIMITED',
}

export enum FernieRating {
  Up = 'UP',
  Down = 'DOWN',
}

export enum FernieDeletedFilter {
  Exclude = 'EXCLUDE',
  Only = 'ONLY',
  Include = 'INCLUDE',
}

export interface FernieConversationFilterInput {
  tenantId?: string | null;
  userEmail?: string | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  outcome?: FernieTurnOutcome | null;
  rating?: FernieRating | null;
  deleted?: FernieDeletedFilter | null;
}

export type GetFernieConversationsArgs = {
  filter: FernieConversationFilterInput;
  page: number;
  pageSize: number;
};

export interface FernieOutcomeCount {
  outcome: string;
  count: number;
}

export interface FernieConversationSummary {
  sessionId: string;
  tenantId: string | null;
  userEmail: string | null;
  title: string | null;
  createdAt: string;
  lastActivity: string;
  turnCount: number;
  isDeleted: boolean;
  signals: FernieOutcomeCount[];
}

export interface FernieConversationPage {
  items: FernieConversationSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface FernieToolCall {
  toolName: string | null;
  status: string | null;
  searchTerm: string | null;
  hitCount: number | null;
  searchFailed: boolean | null;
  failureReason: string | null;
}

export interface FernieMessage {
  id: string;
  role: string | null;
  content: string;
  hasAttachedContext: boolean;
  attachedContext: string | null;
  turnId: string | null;
  timestamp: string;
  tenantId: string | null;
  module: string | null;
  pagePath: string | null;
  model: string | null;
  promptVersion: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  durationMs: number | null;
  outcome: string | null;
  turnOutcome: string;
  rating: string | null;
  ratingReason: string | null;
  ratedAt: string | null;
  rephrasedAfter: boolean | null;
  abandonedAfterDecline: boolean | null;
  toolCalls: FernieToolCall[];
}

export interface FernieConversationDetail {
  session: FernieConversationSummary;
  deletedBy: string | null;
  deletedAt: string | null;
  messages: FernieMessage[];
}

export enum FernieReasonCategory {
  Wrong = 'WRONG',
  DidNotAnswer = 'DID_NOT_ANSWER',
  CouldNotDo = 'COULD_NOT_DO',
  UnhelpfulRedirect = 'UNHELPFUL_REDIRECT',
  Other = 'OTHER',
}

export interface FernieThumbsDownFilterInput {
  tenantId?: string | null;
  from?: string | null;
  to?: string | null;
  reasonCategory?: FernieReasonCategory | null;
  topic?: string | null;
  followUpRequested?: boolean | null;
  reviewed?: boolean | null;
  uncategorized?: boolean | null;
}

export type GetFernieThumbsDownQueueArgs = {
  filter: FernieThumbsDownFilterInput;
  page: number;
  pageSize: number;
};

export interface FernieThumbsDownRow {
  sessionId: string;
  messageId: string;
  tenantId: string | null;
  userDisplay: string;
  question: string;
  answerExcerpt: string;
  outcome: string;
  reasonCategory: string | null;
  reasonText: string | null;
  topic: string | null;
  module: string | null;
  intent: string | null;
  ratedAt: string;
  followUpRequested: boolean;
  forgeTaskId: string | null;
  reviewed: boolean;
  reviewedBy: string | null;
  reviewedAt: string | null;
  notes: string | null;
  linkedHelmTaskId: string | null;
  chatDeleted: boolean;
}

export interface FernieThumbsDownGroup {
  reasonCategory: string;
  topic: string;
  count: number;
  priorCount: number;
}

export interface FernieThumbsDownTotals {
  thumbsDown: number;
  answeredTurns: number;
  ratePer100: number;
  priorRatePer100: number;
}

export interface FernieThumbsDownQueuePage {
  rows: FernieThumbsDownRow[];
  groups: FernieThumbsDownGroup[];
  totals: FernieThumbsDownTotals;
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface SetFernieReviewStateInput {
  sessionId: string;
  messageId: string;
  reviewed: boolean;
  notes: string | null;
}

export interface FernieReviewState {
  sessionId: string;
  messageId: string;
  reviewed: boolean;
  notes: string | null;
  linkedHelmTaskId: string | null;
}

export interface SetFernieReviewStatePayload {
  reviewState: FernieReviewState | null;
  errors: { message: string }[] | null;
}

export interface FileFernieThemeTaskInput {
  projectId: string;
  title: string;
  themeSummary: string;
  reasonCategory: FernieReasonCategory | null;
  topic: string;
  sampleMessageIds: string[];
}

export interface FileFernieThemeTaskPayload {
  helmTaskId: string | null;
  url: string | null;
  errors: { message: string }[] | null;
}

export interface ClearFernieRatingInput {
  sessionId: string;
  messageId: string;
}

export interface ClearFernieRatingPayload {
  clearedAt: string | null;
  errors: { message: string }[] | null;
}

// --- GraphQL Documents ---

const GET_DELETE_TENANT_REQUESTS = `
  query GetDeleteTenantStreamsRequests($after: String) {
    deleteTenantStreamsRequests(first: 100, after: $after) {
      nodes {
        id
        tenantId
        module
        createdFrom
        createdTo
        version
        requestedBy
        requestedAt
        approvedBy
        approvedAt
        status
        started
        ended
        streamsDeleted
        mongoCollectionDeletes
        checkpointPosition
        errorMessage
        cancelledBy
        cancelledAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

const GET_DELETE_TENANT_REQUEST = `
  query GetDeleteTenantStreamsRequest($id: String!) {
    deleteTenantStreamsRequest(id: $id) {
      id
      tenantId
      module
      createdFrom
      createdTo
      version
      requestedBy
      requestedAt
      approvedBy
      approvedAt
      status
      started
      ended
      streamsDeleted
      mongoCollectionDeletes
      checkpointPosition
      errorMessage
      cancelledBy
      cancelledAt
    }
  }
`;

const CREATE_RESET_TENANT_REQUEST = `
  mutation CreateResetTenantRequest($input: CreateResetTenantRequestInput!) {
    createResetTenantRequest(input: $input) {
      request {
        id
        tenantId
        version
        status
        requestedBy
        requestedAt
      }
      errors {
        message
      }
    }
  }
`;

const APPROVE_RESET_TENANT_REQUEST = `
  mutation ApproveResetTenantRequest($input: ApproveResetTenantRequestInput!) {
    approveResetTenantRequest(input: $input) {
      request {
        id
        tenantId
        status
        approvedBy
        approvedAt
      }
      errors {
        message
      }
    }
  }
`;

const RETRY_TENANT_REQUEST = `
  mutation RetryTenantRequest($input: RetryTenantRequestInput!) {
    retryTenantRequest(input: $input) {
      request {
        id
        tenantId
        version
        status
        requestedBy
        requestedAt
      }
      errors {
        message
      }
    }
  }
`;

const CANCEL_RESET_TENANT_REQUEST = `
  mutation CancelResetTenantRequest($input: CancelResetTenantRequestInput!) {
    cancelResetTenantRequest(input: $input) {
      request {
        id
        tenantId
        status
        cancelledBy
        cancelledAt
      }
      errors {
        message
      }
    }
  }
`;


const DELETE_STREAMS_REQUEST_FIELDS = `
  id
  prefix
  version
  startFrom
  status
  requestedBy
  requestedAt
  approvedBy
  approvedAt
  cancelledBy
  cancelledAt
  started
  ended
  streamsDeleted
  checkpointPosition
  errorMessage
`;

const GET_DELETE_STREAMS_REQUESTS = `
  query GetDeleteStreamsRequests($first: Int, $after: String) {
    deleteStreamsRequests(first: $first, after: $after) {
      nodes {
        ${DELETE_STREAMS_REQUEST_FIELDS}
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

const GET_DELETE_STREAMS_REQUEST = `
  query GetDeleteStreamsRequest($id: String!) {
    deleteStreamsRequest(id: $id) {
      ${DELETE_STREAMS_REQUEST_FIELDS}
    }
  }
`;

const GET_LATEST_DELETE_STREAMS_REQUEST = `
  query GetLatestDeleteStreamsRequest($prefix: String!) {
    latestDeleteStreamsRequest(prefix: $prefix) {
      ${DELETE_STREAMS_REQUEST_FIELDS}
    }
  }
`;

const GET_PENDING_DELETE_STREAMS_REQUESTS = `
  query GetPendingDeleteStreamsRequests {
    pendingDeleteStreamsRequests {
      ${DELETE_STREAMS_REQUEST_FIELDS}
    }
  }
`;

const CREATE_DELETE_STREAMS_REQUEST = `
  mutation CreateDeleteStreamsRequest($input: CreateDeleteStreamsRequestInput!) {
    createDeleteStreamsRequest(input: $input) {
      request {
        ${DELETE_STREAMS_REQUEST_FIELDS}
      }
      errors {
        message
      }
    }
  }
`;

const APPROVE_DELETE_STREAMS_REQUEST = `
  mutation ApproveDeleteStreamsRequest($input: ApproveDeleteStreamsRequestInput!) {
    approveDeleteStreamsRequest(input: $input) {
      request {
        ${DELETE_STREAMS_REQUEST_FIELDS}
      }
      errors {
        message
      }
    }
  }
`;

const CANCEL_DELETE_STREAMS_REQUEST = `
  mutation CancelDeleteStreamsRequest($input: CancelDeleteStreamsRequestInput!) {
    cancelDeleteStreamsRequest(input: $input) {
      request {
        ${DELETE_STREAMS_REQUEST_FIELDS}
      }
      errors {
        message
      }
    }
  }
`;

const RESET_DELETE_STREAMS_REQUEST = `
  mutation ResetDeleteStreamsRequest($input: ResetDeleteStreamsRequestInput!) {
    resetDeleteStreamsRequest(input: $input) {
      request {
        ${DELETE_STREAMS_REQUEST_FIELDS}
      }
      errors {
        message
      }
    }
  }
`;


const KEY_VAULT_SECRET_EXISTS = `
  query KeyVaultSecretExists($secretName: String!) {
    keyVaultSecretExists(secretName: $secretName)
  }
`;

const GET_KEY_VAULT_SECRETS = `
  query GetKeyVaultSecrets($prefix: String, $search: String) {
    keyVaultSecrets(prefix: $prefix, search: $search) {
      name
      createdOn
      updatedOn
      tags {
        key
        value
      }
    }
  }
`;

const CREATE_KEY_VAULT_SECRET = `
  mutation CreateKeyVaultSecret($input: CreateKeyVaultSecretInput!) {
    createKeyVaultSecret(input: $input) {
      secret {
        name
        createdOn
        updatedOn
        tags {
          key
          value
        }
      }
      errors {
        message
      }
    }
  }
`;

const DELETE_KEY_VAULT_SECRET = `
  mutation DeleteKeyVaultSecret($input: DeleteKeyVaultSecretInput!) {
    deleteKeyVaultSecret(input: $input) {
      secret {
        name
      }
      errors {
        message
      }
    }
  }
`;

const GET_DAILY_TRANSACTION_SUMMARIES = `
  query GetDailyTransactionSummaries(
    $first: Int
    $after: String
    $where: DailyTransactionSummaryDocumentFilterInput
    $order: [DailyTransactionSummaryDocumentSortInput!]
  ) {
    dailyTransactionSummaries(first: $first, after: $after, where: $where, order: $order) {
      edges {
        cursor
        node {
          id
          tenantId
          date
          customerCode
          locationCode
          itemCode
          itemCost
          unitsSold
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

const GET_AVAILABLE_TENANTS = `
  query GetAvailableTenants {
    availableTenants {
      id
      name
    }
  }
`;

const SWITCH_TENANT = `
  mutation SwitchTenant($input: SwitchTenantInput!) {
    switchTenant(input: $input) {
      success
      reAuthRequired
      selectedTenantId
      errors {
        message
      }
    }
  }
`;

const GET_ENTITY_CHECKPOINTS = `
  query GetEntityCheckpoints(
    $first: Int
    $where: EntityCheckpointDocumentFilterInput
    $order: [EntityCheckpointDocumentSortInput!]
  ) {
    entityCheckpoints(first: $first, where: $where, order: $order) {
      nodes {
        id
        position
      }
      totalCount
    }
  }
`;

const GET_CURRENT_EVENT_STORE_POSITION = `
  query GetCurrentEventStorePosition {
    currentEventStorePosition {
      id
      lastPosition
      lastUpdated
    }
  }
`;

const UPDATE_ENTITY_CHECKPOINT = `
  mutation UpdateEntityCheckpoint($input: UpdateEntityCheckpointInput!) {
    updateEntityCheckpoint(input: $input) {
      checkpoint {
        id
        position
      }
      errors {
        message
      }
    }
  }
`;

const GET_FUSION_AUTH_APPLICATIONS = `
  query GetFusionAuthApplications {
    fusionAuthApplications {
      id
      name
      roles {
        id
        name
        description
        isDefault
        isSuperRole
      }
    }
  }
`;

const GET_FUSION_AUTH_GROUPS = `
  query GetFusionAuthGroups {
    fusionAuthGroups {
      id
      name
      memberCount
      roleCount
    }
  }
`;

const GET_FUSION_AUTH_GROUP = `
  query GetFusionAuthGroup($groupId: UUID!) {
    fusionAuthGroup(groupId: $groupId) {
      success
      data {
        id
        name
        applicationRoles {
          applicationId
          applicationName
          roles {
            id
            name
          }
        }
        members {
          userId
          email
          firstName
          lastName
          missingRegistrationApplicationIds
        }
      }
      errors {
        message
      }
    }
  }
`;

const bulkGroupSelection = `
  allSucceeded
  results {
    itemId
    ok
    errorCode
    errorMessage
    noopReason
  }
  errors {
    message
  }
`;

const ADD_FUSION_AUTH_GROUP_MEMBERS = `
  mutation AddFusionAuthGroupMembers($groupId: UUID!, $userIds: [UUID!]!) {
    addFusionAuthGroupMembers(groupId: $groupId, userIds: $userIds) {${bulkGroupSelection}}
  }
`;

const REMOVE_FUSION_AUTH_GROUP_MEMBERS = `
  mutation RemoveFusionAuthGroupMembers($groupId: UUID!, $userIds: [UUID!]!) {
    removeFusionAuthGroupMembers(groupId: $groupId, userIds: $userIds) {${bulkGroupSelection}}
  }
`;

const ADD_FUSION_AUTH_GROUP_ROLES = `
  mutation AddFusionAuthGroupRoles($groupId: UUID!, $roleIds: [UUID!]!) {
    addFusionAuthGroupRoles(groupId: $groupId, roleIds: $roleIds) {${bulkGroupSelection}}
  }
`;

const REMOVE_FUSION_AUTH_GROUP_ROLES = `
  mutation RemoveFusionAuthGroupRoles($groupId: UUID!, $roleIds: [UUID!]!) {
    removeFusionAuthGroupRoles(groupId: $groupId, roleIds: $roleIds) {${bulkGroupSelection}}
  }
`;

const SEARCH_FUSION_AUTH_USERS = `
  query SearchFusionAuthUsers($input: SearchFusionAuthUsersInput!) {
    fusionAuthUsers(input: $input) {
      success
      total
      users {
        id
        email
        username
        firstName
        lastName
        mobilePhone
        tenantId
        tenantSource
        active
        emailVerified
        registrations {
          applicationId
          roles
        }
      }
      errors {
        message
      }
    }
  }
`;

const GET_FUSION_AUTH_APPLICATION = `
  query GetFusionAuthApplication($input: GetFusionAuthApplicationInput!) {
    fusionAuthApplication(input: $input) {
      success
      data {
        id
        name
        roles {
          id
          name
          description
          isDefault
          isSuperRole
        }
      }
      errors {
        message
      }
    }
  }
`;

const GET_FUSION_AUTH_APPLICATION_USERS = `
  query GetFusionAuthApplicationUsers($input: GetFusionAuthApplicationUsersInput!) {
    fusionAuthApplicationUsers(input: $input) {
      success
      total
      users {
        id
        email
        username
        firstName
        lastName
        roles {
          name
          isOrphan
        }
      }
      errors {
        message
      }
    }
  }
`;

const REGISTER_APPLICATION_TO_USERS = `
  mutation RegisterApplicationToUsers($input: RegisterApplicationToUsersInput!) {
    registerApplicationToUsers(input: $input) {
      allSucceeded
      results {
        userId
        ok
        errorCode
        errorMessage
        noopReason
      }
      errors {
        message
      }
    }
  }
`;

const UNREGISTER_APPLICATION_FROM_USERS = `
  mutation UnregisterApplicationFromUsers($input: UnregisterApplicationFromUsersInput!) {
    unregisterApplicationFromUsers(input: $input) {
      allSucceeded
      results {
        userId
        ok
        errorCode
        errorMessage
        noopReason
      }
      errors {
        message
      }
    }
  }
`;

const ADD_APPLICATION_ROLE_TO_USERS = `
  mutation AddApplicationRoleToUsers($input: AddApplicationRoleToUsersInput!) {
    addApplicationRoleToUsers(input: $input) {
      allSucceeded
      results {
        userId
        ok
        errorCode
        errorMessage
        noopReason
      }
      errors {
        message
      }
    }
  }
`;

const REMOVE_APPLICATION_ROLE_FROM_USERS = `
  mutation RemoveApplicationRoleFromUsers($input: RemoveApplicationRoleFromUsersInput!) {
    removeApplicationRoleFromUsers(input: $input) {
      allSucceeded
      results {
        userId
        ok
        errorCode
        errorMessage
        noopReason
      }
      errors {
        message
      }
    }
  }
`;

const SET_FUSION_AUTH_USER_REGISTRATION_ROLES = `
  mutation SetFusionAuthUserRegistrationRoles($input: SetFusionAuthUserRegistrationRolesInput!) {
    setFusionAuthUserRegistrationRoles(input: $input) {
      success
      registration {
        applicationId
        roles
      }
      errors {
        message
      }
    }
  }
`;

const GET_FUSION_AUTH_USER = `
  query GetFusionAuthUser($input: GetFusionAuthUserInput!) {
    fusionAuthUser(input: $input) {
      success
      data {
        id
        email
        username
        firstName
        lastName
        mobilePhone
        tenantId
        tenantSource
        active
        emailVerified
        registrations {
          applicationId
          roles
        }
      }
      errors {
        message
      }
    }
  }
`;

const UPDATE_FUSION_AUTH_USER = `
  mutation UpdateFusionAuthUser($input: UpdateFusionAuthUserInput!) {
    updateFusionAuthUser(input: $input) {
      success
      user {
        id
        email
        username
        firstName
        lastName
        mobilePhone
        tenantId
        tenantSource
        active
        emailVerified
        registrations {
          applicationId
          roles
        }
      }
      errors {
        message
      }
    }
  }
`;

const REACTIVATE_FUSION_AUTH_USER = `
  mutation ReactivateFusionAuthUser($input: ReactivateFusionAuthUserInput!) {
    reactivateFusionAuthUser(input: $input) {
      success
      user {
        id
        email
        username
        firstName
        lastName
        mobilePhone
        tenantId
        tenantSource
        active
        emailVerified
        registrations {
          applicationId
          roles
        }
      }
      errors {
        message
      }
    }
  }
`;

const DELETE_FUSION_AUTH_USER = `
  mutation DeleteFusionAuthUser($input: DeleteFusionAuthUserInput!) {
    deleteFusionAuthUser(input: $input) {
      success
      deletedUserId
      errors {
        message
      }
    }
  }
`;

const SET_FUSION_AUTH_USER_TENANT = `
  mutation SetFusionAuthUserTenant($input: SetFusionAuthUserTenantInput!) {
    setFusionAuthUserTenant(input: $input) {
      success
      tenantId
      tenantSource
      errors {
        message
      }
    }
  }
`;

const BULK_ROLE_MUTATION_PAYLOAD_FIELDS = `
  allSucceeded
  results {
    userId
    ok
    errorCode
    errorMessage
    noopReason
  }
  errors {
    message
  }
`;

const SET_FUSION_AUTH_USER_TENANTS = `
  mutation SetFusionAuthUserTenants($input: SetFusionAuthUserTenantsInput!) {
    setFusionAuthUserTenants(input: $input) {
      ${BULK_ROLE_MUTATION_PAYLOAD_FIELDS}
    }
  }
`;

const DELETE_FUSION_AUTH_USERS = `
  mutation DeleteFusionAuthUsers($input: DeleteFusionAuthUsersInput!) {
    deleteFusionAuthUsers(input: $input) {
      ${BULK_ROLE_MUTATION_PAYLOAD_FIELDS}
    }
  }
`;

const EVENT_STREAM_AGGREGATE_CONFIG_FIELDS = `
  id
  enabled
  frequencyDays
  executionTime
  timeZone
  trackGreaterThan
  checkpointInterval
  checkpointTimeIntervalMin
  nextExecutionDate
  createdOn
  createdBy
  lastModifiedOn
  lastModifiedBy
`;

const GET_EVENT_STREAM_AGGREGATE_CONFIG = `
  query GetEventStreamAggregateConfig {
    eventStreamAggregateConfig {
      ${EVENT_STREAM_AGGREGATE_CONFIG_FIELDS}
    }
  }
`;

const UPSERT_EVENT_STREAM_AGGREGATE_CONFIG = `
  mutation UpsertEventStreamAggregateConfig($input: EventStreamAggregateConfigInput!) {
    upsertEventStreamAggregateConfig(input: $input) {
      ${EVENT_STREAM_AGGREGATE_CONFIG_FIELDS}
    }
  }
`;

const SNAPSHOT_NODE_FIELDS = `
  id
  snapshottedAt
  checkpointPositionAtSnapshot
  eventCounts {
    key
    value
  }
  streamCounts {
    key
    value
  }
`;

const GET_EVENT_STREAM_AGGREGATE_SNAPSHOTS = `
  query GetEventStreamAggregateSnapshots(
    $first: Int
    $after: String
    $where: EventStreamAggregateSnapshotDocumentFilterInput
    $order: [EventStreamAggregateSnapshotDocumentSortInput!]
  ) {
    eventStreamAggregateSnapshots(
      first: $first
      after: $after
      where: $where
      order: $order
    ) {
      edges {
        node {
          ${SNAPSHOT_NODE_FIELDS}
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

const SCAVENGE_CONFIG_FIELDS = `
  id
  enabled
  frequencyDays
  executionTime
  timeZone
  threadCount
  createdOn
  createdBy
  lastModifiedOn
  lastModifiedBy
`;

const GET_SCAVENGE_CONFIG = `
  query GetScavengeConfig {
    scavengeConfig {
      ${SCAVENGE_CONFIG_FIELDS}
    }
  }
`;

const UPSERT_SCAVENGE_CONFIG = `
  mutation UpsertScavengeConfig($input: ScavengeConfigInput!) {
    upsertScavengeConfig(input: $input) {
      ${SCAVENGE_CONFIG_FIELDS}
    }
  }
`;

const GET_ADMIN_BACKGROUND_SERVICE_POD_STATUSES = `
  query GetAdminBackgroundServicePodStatuses($deploymentNames: [String!]!) {
    adminBackgroundServicePodStatuses(deploymentNames: $deploymentNames) {
      statuses {
        deploymentName
        deploymentExists
        desiredReplicas
        readyReplicas
        availableReplicas
        totalRestartCount
        oldestPodAgeSeconds
        reason
        pods {
          podName
          phase
          ready
          restartCount
          ageSeconds
        }
      }
      errors {
        message
      }
    }
  }
`;

const SET_ADMIN_BACKGROUND_SERVICE_REPLICAS = `
  mutation SetAdminBackgroundServiceReplicas($input: SetBackgroundServiceReplicasInput!) {
    setAdminBackgroundServiceReplicas(input: $input) {
      deploymentStatus {
        deploymentName
        desiredReplicas
        readyReplicas
      }
      errors {
        message
      }
    }
  }
`;

const GET_SCAVENGE_LOGS = `
  query GetScavengeLogs(
    $first: Int
    $after: String
    $where: ScavengeLogDocumentFilterInput
    $order: [ScavengeLogDocumentSortInput!]
  ) {
    scavengeLogs(first: $first, after: $after, where: $where, order: $order) {
      edges {
        node {
          scavengeId
          startTime
          endTime
          checkTime
          duration
          status
          threadCount
          errorMessage
          stopRequested
          stopRequestedBy
          stopped
          scavengeStatus {
            scavengeId
            scavengeResult
            scavengeCompletedPercent
            spaceReclaimed
            chunksScavenged
            error
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;


const GET_ACR_REPOSITORIES = `
  query GetAcrRepositories(
    $first: Int
    $after: String
    $where: AcrRepositoryDocumentFilterInput
    $order: [AcrRepositoryDocumentSortInput!]
  ) {
    acrRepositories(first: $first, after: $after, where: $where, order: $order) {
      edges {
        node {
          id
          repository
          excludedFromCleanup
          tagCount
          lockedTagCount
          lastScannedUtc
          tags
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

const GET_ACR_REPOSITORY_TAGS = `
  query GetAcrRepositoryTags($repository: String!) {
    acrRepositoryTags(repository: $repository) {
      name
      locked
    }
  }
`;

const ACR_CLEANUP_SETTINGS_FIELDS = `
  id
  killSwitchEnabled
  scheduleDays
  scheduleTime
  scheduleTimeZone
  updatedAtUtc
  updatedBy
`;

const ACR_SCAN_TARGET_FIELDS = `
  id
  environment
  kubeContext
  namespace
  tagsToRetain
  updatedAtUtc
  imagePattern
  repositoryPattern
`;

const GET_ACR_CLEANUP_SETTINGS = `
  query GetAcrCleanupSettings {
    acrCleanupSettings {
      ${ACR_CLEANUP_SETTINGS_FIELDS}
    }
  }
`;

const GET_ACR_SCAN_TARGETS = `
  query GetAcrScanTargets {
    acrScanTargets {
      ${ACR_SCAN_TARGET_FIELDS}
    }
  }
`;

const SET_ACR_KILL_SWITCH = `
  mutation SetAcrKillSwitch($input: SetAcrKillSwitchInput!) {
    setAcrKillSwitch(input: $input) {
      settings {
        ${ACR_CLEANUP_SETTINGS_FIELDS}
      }
      errors {
        message
      }
    }
  }
`;

const SET_ACR_CLEANUP_SCHEDULE = `
  mutation SetAcrCleanupSchedule($input: SetAcrCleanupScheduleInput!) {
    setAcrCleanupSchedule(input: $input) {
      settings {
        ${ACR_CLEANUP_SETTINGS_FIELDS}
      }
      errors {
        message
      }
    }
  }
`;

const SET_ACR_REPOSITORY_EXCLUDED = `
  mutation SetAcrRepositoryExcluded($input: SetAcrRepositoryExcludedInput!) {
    setAcrRepositoryExcluded(input: $input) {
      repository {
        id
        repository
        excludedFromCleanup
        tagCount
        lockedTagCount
        lastScannedUtc
        tags
      }
      errors {
        message
      }
    }
  }
`;

const SET_ACR_RETENTION = `
  mutation SetAcrRetention($input: SetAcrRetentionInput!) {
    setAcrRetention(input: $input) {
      scanTarget {
        ${ACR_SCAN_TARGET_FIELDS}
      }
      errors {
        message
      }
    }
  }
`;

const ACR_AUDIT_LOG_NODE_FIELDS = `
  id
  runId
  repository
  tag
  action
  reason
  mode
  timestampUtc
  source
  errorMessage
`;

const GET_ACR_AUDIT_LOG = `
  query GetAcrAuditLog(
    $first: Int
    $after: String
    $where: AcrAuditLogDocumentFilterInput
    $order: [AcrAuditLogDocumentSortInput!]
  ) {
    acrAuditLog(first: $first, after: $after, where: $where, order: $order) {
      edges {
        node {
          ${ACR_AUDIT_LOG_NODE_FIELDS}
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

const ACR_ACTION_COMMAND_FIELDS = `
  id
  repository
  tag
  action
  status
  requestedBy
  requestedAtUtc
  processedAtUtc
  error
`;

const GET_ACR_ACTION_COMMANDS = `
  query GetAcrActionCommands(
    $first: Int
    $after: String
    $where: AcrActionCommandDocumentFilterInput
    $order: [AcrActionCommandDocumentSortInput!]
  ) {
    acrActionCommands(first: $first, after: $after, where: $where, order: $order) {
      edges {
        node {
          ${ACR_ACTION_COMMAND_FIELDS}
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

const ACR_ACTION_COMMAND_PAYLOAD = `
  command {
    ${ACR_ACTION_COMMAND_FIELDS}
  }
  errors {
    message
  }
`;

const LOCK_ACR_TAG = `
  mutation LockAcrTag($input: LockAcrTagInput!) {
    lockAcrTag(input: $input) {
      ${ACR_ACTION_COMMAND_PAYLOAD}
    }
  }
`;

const UNLOCK_ACR_TAG = `
  mutation UnlockAcrTag($input: UnlockAcrTagInput!) {
    unlockAcrTag(input: $input) {
      ${ACR_ACTION_COMMAND_PAYLOAD}
    }
  }
`;

const DELETE_ACR_TAG = `
  mutation DeleteAcrTag($input: DeleteAcrTagInput!) {
    deleteAcrTag(input: $input) {
      ${ACR_ACTION_COMMAND_PAYLOAD}
    }
  }
`;

const GET_REPO_DEPLOYMENT_STATE = `
  query GetRepoDeploymentState($input: GetRepoDeploymentStateInput!) {
    repoDeploymentState(input: $input) {
      states {
        repo
        stageImageTag
        productionImageTag
      }
      errors {
        message
      }
    }
  }
`;

const GET_AKS_NAMESPACE_HEALTH = `
  query GetAksNamespaceHealth($input: GetAksNamespaceHealthInput!) {
    aksNamespaceHealth(input: $input) {
      health {
        namespace
        healthy
        deploymentCount
        unhealthyDeployments
        podCount
        notReadyPods
        deployments {
          deploymentName
          desiredReplicas
          readyReplicas
          updatedReplicas
          rolloutComplete
          reason
        }
        pods {
          podName
          deploymentName
          ready
          restartCount
          reason
        }
      }
      errors {
        message
      }
    }
  }
`;

const GET_PENDING_APPROVALS = `
  query GetPendingApprovals($input: GetPendingApprovalsInput!) {
    pendingApprovals(input: $input) {
      approvals {
        repo
        pipelineDefinitionId
        pipelineName
        runId
        runName
        approvalId
        stage
        requestedBy
        requestedOn
      }
      errors {
        message
      }
    }
  }
`;

const APPROVE_PIPELINE_RUN = `
  mutation ApprovePipelineRun($input: ApprovePipelineRunInput!) {
    approvePipelineRun(input: $input) {
      success
      errors {
        message
      }
    }
  }
`;

const FERNIE_SUMMARY_FIELDS = `
  sessionId
  tenantId
  userEmail
  title
  createdAt
  lastActivity
  turnCount
  isDeleted
  signals {
    outcome
    count
  }
`;

const GET_FERNIE_CONVERSATIONS = `
  query GetFernieConversations($filter: FernieConversationFilterInput, $page: Int!, $pageSize: Int!) {
    fernieConversations(filter: $filter, page: $page, pageSize: $pageSize) {
      items {
        ${FERNIE_SUMMARY_FIELDS}
      }
      totalCount
      page
      pageSize
    }
  }
`;

const GET_FERNIE_THUMBS_DOWN_QUEUE = `
  query GetFernieThumbsDownQueue($filter: FernieThumbsDownFilterInput, $page: Int!, $pageSize: Int!) {
    fernieThumbsDownQueue(filter: $filter, page: $page, pageSize: $pageSize) {
      rows {
        sessionId
        messageId
        tenantId
        userDisplay
        question
        answerExcerpt
        outcome
        reasonCategory
        reasonText
        topic
        module
        intent
        ratedAt
        followUpRequested
        forgeTaskId
        reviewed
        reviewedBy
        reviewedAt
        notes
        linkedHelmTaskId
        chatDeleted
      }
      groups {
        reasonCategory
        topic
        count
        priorCount
      }
      totals {
        thumbsDown
        answeredTurns
        ratePer100
        priorRatePer100
      }
      totalCount
      page
      pageSize
    }
  }
`;

const SET_FERNIE_REVIEW_STATE = `
  mutation SetFernieReviewState($input: SetFernieReviewStateInput!) {
    setFernieReviewState(input: $input) {
      reviewState {
        sessionId
        messageId
        reviewed
        notes
        linkedHelmTaskId
      }
      errors {
        message
      }
    }
  }
`;

const FILE_FERNIE_THEME_TASK = `
  mutation FileFernieThemeTask($input: FileFernieThemeTaskInput!) {
    fileFernieThemeTask(input: $input) {
      helmTaskId
      url
      errors {
        message
      }
    }
  }
`;

const CLEAR_FERNIE_RATING = `
  mutation ClearFernieRating($input: ClearFernieRatingInput!) {
    clearFernieRating(input: $input) {
      clearedAt
      errors {
        message
      }
    }
  }
`;

const GET_FERNIE_CONVERSATION = `
  query GetFernieConversation($sessionId: String!) {
    fernieConversation(sessionId: $sessionId) {
      session {
        ${FERNIE_SUMMARY_FIELDS}
      }
      deletedBy
      deletedAt
      messages {
        id
        role
        content
        hasAttachedContext
        attachedContext
        turnId
        timestamp
        tenantId
        module
        pagePath
        model
        promptVersion
        inputTokens
        outputTokens
        durationMs
        outcome
        turnOutcome
        rating
        ratingReason
        ratedAt
        rephrasedAfter
        abandonedAfterDecline
        toolCalls {
          toolName
          status
          searchTerm
          hitCount
          searchFailed
          failureReason
        }
      }
    }
  }
`;

// --- Test logins ---

export interface TestLoginDocument {
  id: string;
  username: string;
  tenantId: string;
  roles: string[];
  purpose: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface TestLoginError {
  message: string;
}

export interface TestLoginPayload {
  success: boolean;
  testLogin: TestLoginDocument | null;
  errors: TestLoginError[] | null;
}

export interface DeleteTestLoginPayload {
  success: boolean;
  deletedId: string | null;
  errors: TestLoginError[] | null;
}

export interface CreateTestLoginInput {
  username: string;
  tenantId: string;
  roles: string[];
  purpose: string;
}

export interface UpdateTestLoginInput {
  id: string;
  tenantId?: string;
  roles?: string[];
  purpose?: string;
}

const TEST_LOGIN_FIELDS = `
  id
  username
  tenantId
  roles
  purpose
  createdBy
  createdAt
  updatedAt
`;

const GET_TEST_LOGINS = `
  query GetTestLogins {
    testLogins {
      ${TEST_LOGIN_FIELDS}
    }
  }
`;

const CREATE_TEST_LOGIN = `
  mutation CreateTestLogin($input: CreateTestLoginInput!) {
    createTestLogin(input: $input) {
      success
      testLogin {
        ${TEST_LOGIN_FIELDS}
      }
      errors {
        message
      }
    }
  }
`;

const UPDATE_TEST_LOGIN = `
  mutation UpdateTestLogin($input: UpdateTestLoginInput!) {
    updateTestLogin(input: $input) {
      success
      testLogin {
        ${TEST_LOGIN_FIELDS}
      }
      errors {
        message
      }
    }
  }
`;

const DELETE_TEST_LOGIN = `
  mutation DeleteTestLogin($id: String!) {
    deleteTestLogin(id: $id) {
      success
      deletedId
      errors {
        message
      }
    }
  }
`;

// --- Base Query ---

interface ParsedGraphQL {
  data?: unknown;
  errors?: GraphQLErrorLike[];
}

/** Read a response body once, returning parsed JSON if possible and a raw snippet either way */
const readResponseBody = async (response: Response): Promise<{ json?: ParsedGraphQL; snippet?: string }> => {
  const text = await response.text();
  if (!text) return {};
  try {
    return { json: JSON.parse(text) as ParsedGraphQL, snippet: truncateBody(text) };
  } catch {
    return { snippet: truncateBody(text) };
  }
};

/** Make a GraphQL fetch request with current auth headers */
const makeAdminGraphQLRequest = async (document: string, variables?: Record<string, unknown>): Promise<Response> => {
  // Proactive token refresh before each request — log failures so they're visible
  await sfAuth.getAccessToken().catch((err) => {
    console.warn('Proactive token refresh failed:', err instanceof Error ? err.message : err);
  });
  const headers = sfAuth.getAuthHeaders();
  return fetch(Config.adminGraphqlEndpoint, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: document, variables }),
  });
};

const hasAuthErrorMessage = (errors?: GraphQLErrorLike[]): boolean =>
  !!errors?.some((e) => e.message?.toLowerCase().includes('not authorized'));

const tryForceRefresh = async (): Promise<boolean> => {
  try {
    await sfAuth.getAccessToken(true);
    return true;
  } catch {
    return false;
  }
};

const sessionExpiredError = (url: string, operation: string | undefined): RequestError => {
  emitSessionExpired('Your session has expired. Please sign in again.');
  sfAuth.clearSession();
  return buildRequestError({
    kind: 'session-expired',
    endpoint: ENDPOINT_LABEL,
    url,
    operation,
    status: 401,
  });
};

const wsapiAuthRejected = (
  url: string,
  operation: string | undefined,
  bodySnippet?: string,
): RequestError =>
  buildRequestError({
    kind: 'wsapi-auth-rejected',
    endpoint: ENDPOINT_LABEL,
    url,
    operation,
    status: 401,
    bodySnippet,
  });

export const graphqlAdminBaseQuery = async (
  { document, variables }: { document: string; variables?: Record<string, unknown> },
) => {
  const url = Config.adminGraphqlEndpoint;
  const operation = parseOperationName(document);

  let response: Response;
  try {
    response = await withRetry(
      async () => {
        const resp = await makeAdminGraphQLRequest(document, variables);
        if (resp.status >= 500) {
          throw Object.assign(new Error(`Server error: ${resp.status}`), { response: { status: resp.status } });
        }
        return resp;
      },
      {
        maxRetries: 3,
        retryCondition: () => true, // Only 5xx and network errors reach here
      },
    );
  } catch (error) {
    const httpStatus = (error as { response?: { status?: number } })?.response?.status;
    if (httpStatus && httpStatus >= 500) {
      return {
        error: buildRequestError({
          kind: 'http',
          endpoint: ENDPOINT_LABEL,
          url,
          operation,
          status: httpStatus,
          detail: 'retries exhausted',
        }),
      };
    }
    return {
      error: buildRequestError({
        kind: 'network',
        endpoint: ENDPOINT_LABEL,
        url,
        operation,
        detail: error instanceof Error ? error.message : 'Unknown network error',
      }),
    };
  }

  if (response.status === 401) {
    const refreshed = await tryForceRefresh();
    if (!refreshed) {
      return { error: sessionExpiredError(url, operation) };
    }
    const retryResponse = await makeAdminGraphQLRequest(document, variables);
    if (retryResponse.status === 401) {
      const { snippet } = await readResponseBody(retryResponse);
      return { error: wsapiAuthRejected(url, operation, snippet) };
    }
    response = retryResponse;
  }

  const { json, snippet } = await readResponseBody(response);

  if (!json) {
    return {
      error: buildRequestError({
        kind: 'http',
        endpoint: ENDPOINT_LABEL,
        url,
        operation,
        status: response.status,
        bodySnippet: snippet,
        detail: 'non-JSON response',
      }),
    };
  }

  if (json.errors?.length && hasAuthErrorMessage(json.errors)) {
    const refreshed = await tryForceRefresh();
    if (!refreshed) {
      return { error: sessionExpiredError(url, operation) };
    }
    const retryResponse = await makeAdminGraphQLRequest(document, variables);
    const { json: retryJson, snippet: retrySnippet } = await readResponseBody(retryResponse);
    if (retryResponse.status === 401 || (retryJson?.errors && hasAuthErrorMessage(retryJson.errors))) {
      return { error: wsapiAuthRejected(url, operation, retrySnippet) };
    }
    if (!retryJson) {
      return {
        error: buildRequestError({
          kind: 'http',
          endpoint: ENDPOINT_LABEL,
          url,
          operation,
          status: retryResponse.status,
          bodySnippet: retrySnippet,
          detail: 'non-JSON response',
        }),
      };
    }
    if (retryJson.errors?.length) {
      return {
        error: buildRequestError({
          kind: 'graphql',
          endpoint: ENDPOINT_LABEL,
          url,
          operation,
          status: retryResponse.status,
          graphqlErrors: retryJson.errors,
        }),
      };
    }
    return { data: retryJson.data };
  }

  if (json.errors?.length) {
    return {
      error: buildRequestError({
        kind: 'graphql',
        endpoint: ENDPOINT_LABEL,
        url,
        operation,
        status: response.status,
        graphqlErrors: json.errors,
      }),
    };
  }

  return { data: json.data };
};

/**
 * Signature of a bound admin base query — matches both {@link graphqlAdminBaseQuery}
 * and the `baseQuery` handed to an RTK `queryFn`, so the paging helper can be
 * unit-tested with a stub.
 */
type AdminBaseQuery = (arg: {
  document: string;
  variables?: Record<string, unknown>;
}) => Promise<{ data?: unknown; error?: RequestError }>;

/**
 * Page through every cursor of `deleteTenantStreamsRequests` and accumulate all
 * requests. The admin API caps a single page at 100 (DefaultPageSize 25) and the
 * grid has no pagination UI, so without this requests past the first page are
 * silently dropped — hiding active requests that still block creation
 * ("a request already exists" with nothing visible in the list).
 */
export const pageAllResetTenantRequests = async (
  baseQuery: AdminBaseQuery,
): Promise<{ data: DeleteTenantStreamsRequestDocument[] } | { error: RequestError }> => {
  const nodes: DeleteTenantStreamsRequestDocument[] = [];
  let after: string | null = null;
  // Bound the loop as a safety net against a server that never reports the last page.
  for (let page = 0; page < 1000; page++) {
    const result = await baseQuery({
      document: GET_DELETE_TENANT_REQUESTS,
      variables: { after },
    });
    if (result.error) {
      return { error: result.error };
    }
    const connection = (result.data as {
      deleteTenantStreamsRequests: DeleteTenantStreamsRequestsConnection;
    }).deleteTenantStreamsRequests;
    nodes.push(...connection.nodes);
    if (!connection.pageInfo?.hasNextPage) {
      break;
    }
    if (!connection.pageInfo.endCursor) {
      // Defensive: the connection contract should always supply a cursor when
      // more pages exist. If it doesn't, stop instead of re-requesting the first
      // page (after stays null) in a loop, which would duplicate rows up to the
      // safety cap.
      return {
        error: buildRequestError({
          kind: 'graphql',
          endpoint: ENDPOINT_LABEL,
          url: Config.adminGraphqlEndpoint,
          operation: parseOperationName(GET_DELETE_TENANT_REQUESTS),
          graphqlErrors: [
            { message: 'deleteTenantStreamsRequests reported hasNextPage but returned no endCursor' },
          ],
        }),
      };
    }
    after = connection.pageInfo.endCursor;
  }
  return { data: nodes };
};

const MAX_DELETE_STREAMS_PAGES = 1000;

export const pageAllDeleteStreamsRequests = async (
  baseQuery: AdminBaseQuery,
): Promise<{ data: DeleteStreamsRequestDocument[] } | { error: RequestError }> => {
  const nodes: DeleteStreamsRequestDocument[] = [];
  let after: string | null = null;
  for (let page = 0; page < MAX_DELETE_STREAMS_PAGES; page++) {
    const result = await baseQuery({
      document: GET_DELETE_STREAMS_REQUESTS,
      variables: { after, first: 100 },
    });
    if (result.error) {
      return { error: result.error };
    }
    const connection = (result.data as {
      deleteStreamsRequests: DeleteStreamsRequestsConnection;
    }).deleteStreamsRequests;
    nodes.push(...connection.nodes);
    if (!connection.pageInfo?.hasNextPage) {
      break;
    }
    if (!connection.pageInfo.endCursor) {
      return {
        error: buildRequestError({
          kind: 'graphql',
          endpoint: ENDPOINT_LABEL,
          url: Config.adminGraphqlEndpoint,
          operation: parseOperationName(GET_DELETE_STREAMS_REQUESTS),
          graphqlErrors: [
            { message: 'deleteStreamsRequests reported hasNextPage but returned no endCursor' },
          ],
        }),
      };
    }
    after = connection.pageInfo.endCursor;
  }
  return { data: nodes };
};

// --- API Slice ---

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: graphqlAdminBaseQuery,
  tagTypes: [
    'ResetTenantRequest',
    'DeleteStreamsRequest',
    'KeyVaultSecret',
    'DailyTransactionSummary',
    'EntityCheckpoint',
    'AvailableTenant',
    'FusionAuthApplication',
    'FusionAuthUser',
    'FusionAuthGroup',
    'ScavengeConfig',
    'ScavengeLog',
    'EventStreamAggregateConfig',
    'EventStreamAggregateSnapshot',
    'BackgroundServiceDeployment',
    'AcrRepository',
    'AcrRepositoryTags',
    'AcrCleanupSettings',
    'AcrScanTarget',
    'AcrAuditLog',
    'AcrActionCommand',
    'RepoDeploymentState',
    'AksNamespaceHealth',
    'PendingApproval',
    'FernieConversation',
    'FernieThumbsDownQueue',
    'TestLogin',
  ],
  endpoints: (builder) => ({
    getResetTenantRequests: builder.query<DeleteTenantStreamsRequestDocument[], void>({
      // Page through every cursor so the grid shows the full set — see pageAllResetTenantRequests.
      queryFn: (_arg, _api, _extraOptions, baseQuery) =>
        pageAllResetTenantRequests(baseQuery as AdminBaseQuery),
      providesTags: ['ResetTenantRequest'],
    }),

    getResetTenantRequest: builder.query<DeleteTenantStreamsRequestDocument | null, string>({
      query: (id) => ({ document: GET_DELETE_TENANT_REQUEST, variables: { id } }),
      transformResponse: (response: { deleteTenantStreamsRequest: DeleteTenantStreamsRequestDocument | null }) =>
        response.deleteTenantStreamsRequest,
      providesTags: ['ResetTenantRequest'],
    }),

    createResetTenantRequest: builder.mutation<
      ResetTenantRequestPayload,
      { tenantId: string; module?: string; createdFrom?: string | null; createdTo?: string | null }
    >({
      query: (input) => ({
        document: CREATE_RESET_TENANT_REQUEST,
        variables: { input },
      }),
      transformResponse: (response: { createResetTenantRequest: ResetTenantRequestPayload }) =>
        response.createResetTenantRequest,
      invalidatesTags: ['ResetTenantRequest'],
    }),

    approveResetTenantRequest: builder.mutation<ResetTenantRequestPayload, { tenantId: string }>({
      query: (input) => ({
        document: APPROVE_RESET_TENANT_REQUEST,
        variables: { input },
      }),
      transformResponse: (response: { approveResetTenantRequest: ResetTenantRequestPayload }) =>
        response.approveResetTenantRequest,
      invalidatesTags: ['ResetTenantRequest'],
    }),

    retryTenantRequest: builder.mutation<ResetTenantRequestPayload, { tenantId: string }>({
      query: (input) => ({
        document: RETRY_TENANT_REQUEST,
        variables: { input },
      }),
      transformResponse: (response: { retryTenantRequest: ResetTenantRequestPayload }) =>
        response.retryTenantRequest,
      invalidatesTags: ['ResetTenantRequest'],
    }),

    cancelResetTenantRequest: builder.mutation<ResetTenantRequestPayload, { tenantId: string }>({
      query: (input) => ({
        document: CANCEL_RESET_TENANT_REQUEST,
        variables: { input },
      }),
      transformResponse: (response: { cancelResetTenantRequest: ResetTenantRequestPayload }) =>
        response.cancelResetTenantRequest,
      invalidatesTags: ['ResetTenantRequest'],
    }),

    getDeleteStreamsRequests: builder.query<DeleteStreamsRequestDocument[], void>({
      // Page through every cursor so the grid shows the full set — see pageAllDeleteStreamsRequests.
      queryFn: (_arg, _api, _extraOptions, baseQuery) =>
        pageAllDeleteStreamsRequests(baseQuery as AdminBaseQuery),
      providesTags: ['DeleteStreamsRequest'],
    }),

    getDeleteStreamsRequest: builder.query<DeleteStreamsRequestDocument | null, string>({
      query: (id) => ({ document: GET_DELETE_STREAMS_REQUEST, variables: { id } }),
      transformResponse: (response: { deleteStreamsRequest: DeleteStreamsRequestDocument | null }) =>
        response.deleteStreamsRequest,
      providesTags: ['DeleteStreamsRequest'],
    }),

    getLatestDeleteStreamsRequest: builder.query<DeleteStreamsRequestDocument | null, string>({
      query: (prefix) => ({ document: GET_LATEST_DELETE_STREAMS_REQUEST, variables: { prefix } }),
      transformResponse: (response: { latestDeleteStreamsRequest: DeleteStreamsRequestDocument | null }) =>
        response.latestDeleteStreamsRequest,
      providesTags: ['DeleteStreamsRequest'],
    }),

    getPendingDeleteStreamsRequests: builder.query<DeleteStreamsRequestDocument[], void>({
      query: () => ({ document: GET_PENDING_DELETE_STREAMS_REQUESTS }),
      transformResponse: (response: { pendingDeleteStreamsRequests: DeleteStreamsRequestDocument[] }) =>
        response.pendingDeleteStreamsRequests,
      providesTags: ['DeleteStreamsRequest'],
    }),

    createDeleteStreamsRequest: builder.mutation<
      DeleteStreamsRequestPayload,
      { prefix: string; startFrom: DeleteStreamsStartFrom }
    >({
      query: (input) => ({
        document: CREATE_DELETE_STREAMS_REQUEST,
        variables: { input },
      }),
      transformResponse: (response: { createDeleteStreamsRequest: DeleteStreamsRequestPayload }) =>
        response.createDeleteStreamsRequest,
      invalidatesTags: ['DeleteStreamsRequest'],
    }),

    approveDeleteStreamsRequest: builder.mutation<DeleteStreamsRequestPayload, { prefix: string }>({
      query: (input) => ({
        document: APPROVE_DELETE_STREAMS_REQUEST,
        variables: { input },
      }),
      transformResponse: (response: { approveDeleteStreamsRequest: DeleteStreamsRequestPayload }) =>
        response.approveDeleteStreamsRequest,
      invalidatesTags: ['DeleteStreamsRequest'],
    }),

    cancelDeleteStreamsRequest: builder.mutation<DeleteStreamsRequestPayload, { prefix: string }>({
      query: (input) => ({
        document: CANCEL_DELETE_STREAMS_REQUEST,
        variables: { input },
      }),
      transformResponse: (response: { cancelDeleteStreamsRequest: DeleteStreamsRequestPayload }) =>
        response.cancelDeleteStreamsRequest,
      invalidatesTags: ['DeleteStreamsRequest'],
    }),

    resetDeleteStreamsRequest: builder.mutation<DeleteStreamsRequestPayload, { prefix: string }>({
      query: (input) => ({
        document: RESET_DELETE_STREAMS_REQUEST,
        variables: { input },
      }),
      transformResponse: (response: { resetDeleteStreamsRequest: DeleteStreamsRequestPayload }) =>
        response.resetDeleteStreamsRequest,
      invalidatesTags: ['DeleteStreamsRequest'],
    }),

    keyVaultSecretExists: builder.query<boolean, string>({
      query: (secretName) => ({
        document: KEY_VAULT_SECRET_EXISTS,
        variables: { secretName },
      }),
      transformResponse: (response: { keyVaultSecretExists: boolean }) =>
        response.keyVaultSecretExists,
    }),

    getKeyVaultSecrets: builder.query<KeyVaultSecretInfo[], { prefix?: string; search?: string }>({
      query: ({ prefix, search }) => ({
        document: GET_KEY_VAULT_SECRETS,
        variables: { prefix, search },
      }),
      transformResponse: (response: { keyVaultSecrets: KeyVaultSecretInfo[] }) =>
        response.keyVaultSecrets,
      providesTags: ['KeyVaultSecret'],
    }),

    createKeyVaultSecret: builder.mutation<KeyVaultSecretPayload, { input: CreateKeyVaultSecretInput }>({
      query: (variables) => ({
        document: CREATE_KEY_VAULT_SECRET,
        variables,
      }),
      transformResponse: (response: { createKeyVaultSecret: KeyVaultSecretPayload }) =>
        response.createKeyVaultSecret,
      invalidatesTags: ['KeyVaultSecret'],
    }),

    deleteKeyVaultSecret: builder.mutation<KeyVaultSecretPayload, { input: { secretName: string } }>({
      query: (variables) => ({
        document: DELETE_KEY_VAULT_SECRET,
        variables,
      }),
      transformResponse: (response: { deleteKeyVaultSecret: KeyVaultSecretPayload }) =>
        response.deleteKeyVaultSecret,
      invalidatesTags: ['KeyVaultSecret'],
    }),

    getDailyTransactionSummaries: builder.query<DailyTransactionSummariesConnection, GetDailyTransactionSummariesArgs>({
      query: (args) => ({
        document: GET_DAILY_TRANSACTION_SUMMARIES,
        variables: {
          first: args.first ?? 100,
          after: args.after ?? null,
          where: args.where ?? null,
          order: args.order ?? null,
        },
      }),
      transformResponse: (response: { dailyTransactionSummaries: DailyTransactionSummariesConnection | null }) =>
        response.dailyTransactionSummaries ?? {
          edges: [],
          pageInfo: { hasNextPage: false, endCursor: null },
          totalCount: 0,
        },
      providesTags: ['DailyTransactionSummary'],
    }),

    getAvailableTenants: builder.query<AvailableTenant[], void>({
      query: () => ({ document: GET_AVAILABLE_TENANTS }),
      transformResponse: (response: { availableTenants: AvailableTenant[] }) =>
        response.availableTenants,
      providesTags: ['AvailableTenant'],
    }),

    switchTenant: builder.mutation<SwitchTenantPayload, { tenantId: string }>({
      query: (input) => ({
        document: SWITCH_TENANT,
        variables: { input },
      }),
      transformResponse: (response: { switchTenant: SwitchTenantPayload }) =>
        response.switchTenant,
      // Do not invalidate anything here — the JWT still carries the old tenant claim
      // until the user re-authenticates, so any cached data is still valid for it.
    }),

    getEntityCheckpoints: builder.query<
      EntityCheckpointDocument[],
      { idContains?: string; first?: number } | void
    >({
      query: (args) => {
        const idContains = args?.idContains?.trim();
        const where = idContains ? { id: { contains: idContains } } : undefined;
        return {
          document: GET_ENTITY_CHECKPOINTS,
          variables: {
            first: args?.first ?? 100,
            where,
            order: [{ id: 'ASC' }],
          },
        };
      },
      transformResponse: (response: { entityCheckpoints: EntityCheckpointsConnection | null }) =>
        response.entityCheckpoints?.nodes ?? [],
      providesTags: ['EntityCheckpoint'],
    }),

    getCurrentEventStorePosition: builder.query<EventStoreDocument | null, void>({
      query: () => ({ document: GET_CURRENT_EVENT_STORE_POSITION }),
      transformResponse: (response: { currentEventStorePosition: EventStoreDocument | null }) =>
        response.currentEventStorePosition,
      providesTags: ['EntityCheckpoint'],
    }),

    updateEntityCheckpoint: builder.mutation<
      AdminEntityCheckpointPayload,
      UpdateEntityCheckpointInput
    >({
      query: (input) => ({
        document: UPDATE_ENTITY_CHECKPOINT,
        variables: { input },
      }),
      transformResponse: (response: { updateEntityCheckpoint: AdminEntityCheckpointPayload }) =>
        response.updateEntityCheckpoint,
      invalidatesTags: ['EntityCheckpoint'],
    }),

    getFusionAuthApplications: builder.query<FusionAuthApplication[], void>({
      query: () => ({ document: GET_FUSION_AUTH_APPLICATIONS }),
      transformResponse: (response: { fusionAuthApplications: FusionAuthApplication[] }) =>
        response.fusionAuthApplications,
      providesTags: ['FusionAuthApplication'],
    }),

    getFusionAuthGroups: builder.query<FusionAuthGroupSummary[], void>({
      query: () => ({ document: GET_FUSION_AUTH_GROUPS }),
      transformResponse: (response: { fusionAuthGroups: FusionAuthGroupSummary[] }) =>
        response.fusionAuthGroups,
      providesTags: ['FusionAuthGroup'],
    }),

    getFusionAuthGroup: builder.query<FusionAuthGroupPayload, { groupId: string }>({
      query: (variables) => ({ document: GET_FUSION_AUTH_GROUP, variables: { ...variables } }),
      transformResponse: (response: { fusionAuthGroup: FusionAuthGroupPayload }) =>
        response.fusionAuthGroup,
      providesTags: ['FusionAuthGroup'],
    }),

    addFusionAuthGroupMembers: builder.mutation<BulkGroupMutationPayload, GroupMembersInput>({
      query: (variables) => ({
        document: ADD_FUSION_AUTH_GROUP_MEMBERS,
        variables: { ...variables },
      }),
      transformResponse: (response: { addFusionAuthGroupMembers: BulkGroupMutationPayload }) =>
        response.addFusionAuthGroupMembers,
      invalidatesTags: ['FusionAuthGroup'],
    }),

    removeFusionAuthGroupMembers: builder.mutation<BulkGroupMutationPayload, GroupMembersInput>({
      query: (variables) => ({
        document: REMOVE_FUSION_AUTH_GROUP_MEMBERS,
        variables: { ...variables },
      }),
      transformResponse: (response: { removeFusionAuthGroupMembers: BulkGroupMutationPayload }) =>
        response.removeFusionAuthGroupMembers,
      invalidatesTags: ['FusionAuthGroup'],
    }),

    addFusionAuthGroupRoles: builder.mutation<BulkGroupMutationPayload, GroupRolesInput>({
      query: (variables) => ({
        document: ADD_FUSION_AUTH_GROUP_ROLES,
        variables: { ...variables },
      }),
      transformResponse: (response: { addFusionAuthGroupRoles: BulkGroupMutationPayload }) =>
        response.addFusionAuthGroupRoles,
      invalidatesTags: ['FusionAuthGroup'],
    }),

    removeFusionAuthGroupRoles: builder.mutation<BulkGroupMutationPayload, GroupRolesInput>({
      query: (variables) => ({
        document: REMOVE_FUSION_AUTH_GROUP_ROLES,
        variables: { ...variables },
      }),
      transformResponse: (response: { removeFusionAuthGroupRoles: BulkGroupMutationPayload }) =>
        response.removeFusionAuthGroupRoles,
      invalidatesTags: ['FusionAuthGroup'],
    }),

    searchFusionAuthUsers: builder.query<FusionAuthUserSearchPayload, SearchFusionAuthUsersInput>({
      query: (input) => ({
        document: SEARCH_FUSION_AUTH_USERS,
        variables: { input },
      }),
      transformResponse: (response: { fusionAuthUsers: FusionAuthUserSearchPayload }) =>
        response.fusionAuthUsers,
      providesTags: ['FusionAuthUser'],
    }),

    getFusionAuthUser: builder.query<FusionAuthUserPayload, GetFusionAuthUserInput>({
      query: (input) => ({
        document: GET_FUSION_AUTH_USER,
        variables: { input },
      }),
      transformResponse: (response: { fusionAuthUser: FusionAuthUserPayload }) =>
        response.fusionAuthUser,
      providesTags: ['FusionAuthUser'],
    }),

    updateFusionAuthUser: builder.mutation<
      UpdateFusionAuthUserPayload,
      UpdateFusionAuthUserInput
    >({
      query: (input) => ({
        document: UPDATE_FUSION_AUTH_USER,
        variables: { input },
      }),
      transformResponse: (response: { updateFusionAuthUser: UpdateFusionAuthUserPayload }) =>
        response.updateFusionAuthUser,
      invalidatesTags: ['FusionAuthUser'],
    }),

    reactivateFusionAuthUser: builder.mutation<
      ReactivateFusionAuthUserPayload,
      ReactivateFusionAuthUserInput
    >({
      query: (input) => ({
        document: REACTIVATE_FUSION_AUTH_USER,
        variables: { input },
      }),
      transformResponse: (response: {
        reactivateFusionAuthUser: ReactivateFusionAuthUserPayload;
      }) => response.reactivateFusionAuthUser,
      invalidatesTags: ['FusionAuthUser'],
    }),

    deleteFusionAuthUser: builder.mutation<
      DeleteFusionAuthUserPayload,
      DeleteFusionAuthUserInput
    >({
      query: (input) => ({
        document: DELETE_FUSION_AUTH_USER,
        variables: { input },
      }),
      transformResponse: (response: { deleteFusionAuthUser: DeleteFusionAuthUserPayload }) =>
        response.deleteFusionAuthUser,
      invalidatesTags: ['FusionAuthUser'],
    }),

    setFusionAuthUserTenant: builder.mutation<
      SetFusionAuthUserTenantPayload,
      SetFusionAuthUserTenantInput
    >({
      query: (input) => ({
        document: SET_FUSION_AUTH_USER_TENANT,
        variables: { input },
      }),
      transformResponse: (response: {
        setFusionAuthUserTenant: SetFusionAuthUserTenantPayload;
      }) => response.setFusionAuthUserTenant,
      invalidatesTags: ['FusionAuthUser'],
    }),

    // SuperAdmin-only; admin-api caps a single call at 200 ids, so callers chunk with @/utils/chunk.
    setFusionAuthUserTenants: builder.mutation<
      BulkRoleMutationPayload,
      SetFusionAuthUserTenantsInput
    >({
      query: (input) => ({
        document: SET_FUSION_AUTH_USER_TENANTS,
        variables: { input },
      }),
      transformResponse: (response: { setFusionAuthUserTenants: BulkRoleMutationPayload }) =>
        response.setFusionAuthUserTenants,
      invalidatesTags: ['FusionAuthUser'],
    }),

    // SuperAdmin-only hard delete; admin-api caps a single call at 200 ids, so callers chunk with @/utils/chunk. An unknown id is Ok with noopReason ALREADY_DELETED, so a re-run is safe.
    deleteFusionAuthUsers: builder.mutation<BulkRoleMutationPayload, DeleteFusionAuthUsersInput>({
      query: (input) => ({
        document: DELETE_FUSION_AUTH_USERS,
        variables: { input },
      }),
      transformResponse: (response: { deleteFusionAuthUsers: BulkRoleMutationPayload }) =>
        response.deleteFusionAuthUsers,
      invalidatesTags: ['FusionAuthUser'],
    }),

    getFusionAuthApplication: builder.query<FusionAuthApplicationPayload, GetFusionAuthApplicationInput>({
      query: (input) => ({
        document: GET_FUSION_AUTH_APPLICATION,
        variables: { input },
      }),
      transformResponse: (response: { fusionAuthApplication: FusionAuthApplicationPayload }) =>
        response.fusionAuthApplication,
      providesTags: ['FusionAuthApplication'],
    }),

    getFusionAuthApplicationUsers: builder.query<ApplicationUsersPayload, GetFusionAuthApplicationUsersInput>({
      query: (input) => ({
        document: GET_FUSION_AUTH_APPLICATION_USERS,
        variables: { input },
      }),
      transformResponse: (response: { fusionAuthApplicationUsers: ApplicationUsersPayload }) =>
        response.fusionAuthApplicationUsers,
      providesTags: ['FusionAuthUser'],
    }),

    registerApplicationToUsers: builder.mutation<BulkRoleMutationPayload, RegisterApplicationToUsersInput>({
      query: (input) => ({
        document: REGISTER_APPLICATION_TO_USERS,
        variables: { input },
      }),
      transformResponse: (response: { registerApplicationToUsers: BulkRoleMutationPayload }) =>
        response.registerApplicationToUsers,
    }),

    unregisterApplicationFromUsers: builder.mutation<BulkRoleMutationPayload, UnregisterApplicationFromUsersInput>({
      query: (input) => ({
        document: UNREGISTER_APPLICATION_FROM_USERS,
        variables: { input },
      }),
      transformResponse: (response: { unregisterApplicationFromUsers: BulkRoleMutationPayload }) =>
        response.unregisterApplicationFromUsers,
    }),

    addApplicationRoleToUsers: builder.mutation<BulkRoleMutationPayload, AddApplicationRoleToUsersInput>({
      query: (input) => ({
        document: ADD_APPLICATION_ROLE_TO_USERS,
        variables: { input },
      }),
      transformResponse: (response: { addApplicationRoleToUsers: BulkRoleMutationPayload }) =>
        response.addApplicationRoleToUsers,
    }),

    removeApplicationRoleFromUsers: builder.mutation<BulkRoleMutationPayload, RemoveApplicationRoleFromUsersInput>({
      query: (input) => ({
        document: REMOVE_APPLICATION_ROLE_FROM_USERS,
        variables: { input },
      }),
      transformResponse: (response: { removeApplicationRoleFromUsers: BulkRoleMutationPayload }) =>
        response.removeApplicationRoleFromUsers,
    }),

    setFusionAuthUserRegistrationRoles: builder.mutation<
      SetFusionAuthUserRegistrationRolesPayload,
      SetFusionAuthUserRegistrationRolesInput
    >({
      query: (input) => ({
        document: SET_FUSION_AUTH_USER_REGISTRATION_ROLES,
        variables: { input },
      }),
      transformResponse: (response: {
        setFusionAuthUserRegistrationRoles: SetFusionAuthUserRegistrationRolesPayload;
      }) => response.setFusionAuthUserRegistrationRoles,
      invalidatesTags: ['FusionAuthUser'],
    }),

    getScavengeConfig: builder.query<ScavengeConfigDocument | null, void>({
      query: () => ({ document: GET_SCAVENGE_CONFIG }),
      transformResponse: (response: { scavengeConfig: ScavengeConfigDocument | null }) =>
        response.scavengeConfig,
      providesTags: ['ScavengeConfig'],
    }),

    upsertScavengeConfig: builder.mutation<ScavengeConfigDocument, ScavengeConfigInput>({
      query: (input) => ({
        document: UPSERT_SCAVENGE_CONFIG,
        variables: { input },
      }),
      transformResponse: (response: { upsertScavengeConfig: ScavengeConfigDocument }) =>
        response.upsertScavengeConfig,
      invalidatesTags: ['ScavengeConfig'],
    }),

    getScavengeLogs: builder.query<ScavengeLogsConnection, GetScavengeLogsArgs>({
      query: (args) => ({
        document: GET_SCAVENGE_LOGS,
        variables: {
          first: args.first ?? 50,
          after: args.after,
          where: args.where,
          order: args.order,
        },
      }),
      transformResponse: (response: { scavengeLogs: ScavengeLogsConnection }) =>
        response.scavengeLogs,
      providesTags: ['ScavengeLog'],
    }),

    getEventStreamAggregateConfig: builder.query<EventStreamAggregateConfigDocument | null, void>({
      query: () => ({ document: GET_EVENT_STREAM_AGGREGATE_CONFIG }),
      transformResponse: (response: {
        eventStreamAggregateConfig: EventStreamAggregateConfigDocument | null;
      }) => response.eventStreamAggregateConfig,
      providesTags: ['EventStreamAggregateConfig'],
    }),

    upsertEventStreamAggregateConfig: builder.mutation<
      EventStreamAggregateConfigDocument,
      EventStreamAggregateConfigInput
    >({
      query: (input) => ({
        document: UPSERT_EVENT_STREAM_AGGREGATE_CONFIG,
        variables: { input },
      }),
      transformResponse: (response: {
        upsertEventStreamAggregateConfig: EventStreamAggregateConfigDocument;
      }) => response.upsertEventStreamAggregateConfig,
      invalidatesTags: ['EventStreamAggregateConfig'],
    }),

    setAdminBackgroundServiceReplicas: builder.mutation<
      BackgroundServiceDeploymentStatusPayload,
      SetAdminBackgroundServiceReplicasInput
    >({
      query: (input) => ({
        document: SET_ADMIN_BACKGROUND_SERVICE_REPLICAS,
        variables: { input },
      }),
      transformResponse: (response: {
        setAdminBackgroundServiceReplicas: BackgroundServiceDeploymentStatusPayload;
      }) => response.setAdminBackgroundServiceReplicas,
      invalidatesTags: ['BackgroundServiceDeployment'],
    }),

    getAdminBackgroundServicePodStatuses: builder.query<
      AdminBackgroundServicePodStatusesPayload,
      { deploymentNames: string[] }
    >({
      query: ({ deploymentNames }) => ({
        document: GET_ADMIN_BACKGROUND_SERVICE_POD_STATUSES,
        variables: { deploymentNames },
      }),
      transformResponse: (response: {
        adminBackgroundServicePodStatuses: AdminBackgroundServicePodStatusesPayload;
      }) => response.adminBackgroundServicePodStatuses,
      providesTags: ['BackgroundServiceDeployment'],
    }),

    getEventStreamAggregateSnapshots: builder.query<
      EventStreamAggregateSnapshotsConnection,
      GetEventStreamAggregateSnapshotsArgs
    >({
      query: (args) => ({
        document: GET_EVENT_STREAM_AGGREGATE_SNAPSHOTS,
        variables: {
          first: args.first ?? 50,
          after: args.after,
          where: args.where,
          order: args.order,
        },
      }),
      transformResponse: (response: {
        eventStreamAggregateSnapshots: EventStreamAggregateSnapshotsConnection;
      }) => response.eventStreamAggregateSnapshots,
      providesTags: ['EventStreamAggregateSnapshot'],
    }),

    getAcrRepositories: builder.query<AcrRepositoriesConnection, GetAcrRepositoriesArgs | void>({
      query: (args) => ({
        document: GET_ACR_REPOSITORIES,
        variables: {
          first: args?.first ?? 200,
          after: args?.after ?? null,
          where: args?.where ?? null,
          order: args?.order ?? null,
        },
      }),
      transformResponse: (response: { acrRepositories: AcrRepositoriesConnection | null }) =>
        response.acrRepositories ?? {
          edges: [],
          pageInfo: { hasNextPage: false, endCursor: null },
          totalCount: 0,
        },
      providesTags: ['AcrRepository'],
    }),

    getAcrRepositoryTags: builder.query<AcrRepositoryTag[], string>({
      query: (repository) => ({
        document: GET_ACR_REPOSITORY_TAGS,
        variables: { repository },
      }),
      transformResponse: (response: { acrRepositoryTags: AcrRepositoryTag[] | null }) =>
        response.acrRepositoryTags ?? [],
      providesTags: ['AcrRepositoryTags'],
    }),

    getAcrCleanupSettings: builder.query<AcrCleanupSettingsDocument, void>({
      query: () => ({ document: GET_ACR_CLEANUP_SETTINGS }),
      transformResponse: (response: { acrCleanupSettings: AcrCleanupSettingsDocument }) =>
        response.acrCleanupSettings,
      providesTags: ['AcrCleanupSettings'],
    }),

    getAcrScanTargets: builder.query<AcrScanTargetDocument[], void>({
      query: () => ({ document: GET_ACR_SCAN_TARGETS }),
      transformResponse: (response: { acrScanTargets: AcrScanTargetDocument[] | null }) =>
        response.acrScanTargets ?? [],
      providesTags: ['AcrScanTarget'],
    }),

    setAcrKillSwitch: builder.mutation<AcrCleanupSettingsPayload, SetAcrKillSwitchInput>({
      query: (input) => ({
        document: SET_ACR_KILL_SWITCH,
        variables: { input },
      }),
      transformResponse: (response: { setAcrKillSwitch: AcrCleanupSettingsPayload }) =>
        response.setAcrKillSwitch,
      invalidatesTags: ['AcrCleanupSettings'],
    }),

    setAcrCleanupSchedule: builder.mutation<AcrCleanupSettingsPayload, SetAcrCleanupScheduleInput>({
      query: (input) => ({
        document: SET_ACR_CLEANUP_SCHEDULE,
        variables: { input },
      }),
      transformResponse: (response: { setAcrCleanupSchedule: AcrCleanupSettingsPayload }) =>
        response.setAcrCleanupSchedule,
      invalidatesTags: ['AcrCleanupSettings'],
    }),

    setAcrRepositoryExcluded: builder.mutation<AcrRepositoryPayload, SetAcrRepositoryExcludedInput>({
      query: (input) => ({
        document: SET_ACR_REPOSITORY_EXCLUDED,
        variables: { input },
      }),
      transformResponse: (response: { setAcrRepositoryExcluded: AcrRepositoryPayload }) =>
        response.setAcrRepositoryExcluded,
      invalidatesTags: ['AcrRepository'],
    }),

    setAcrRetention: builder.mutation<AcrScanTargetPayload, SetAcrRetentionInput>({
      query: (input) => ({
        document: SET_ACR_RETENTION,
        variables: { input },
      }),
      transformResponse: (response: { setAcrRetention: AcrScanTargetPayload }) =>
        response.setAcrRetention,
      invalidatesTags: ['AcrScanTarget'],
    }),

    getAcrAuditLog: builder.query<AcrAuditLogConnection, GetAcrAuditLogArgs | void>({
      query: (args) => ({
        document: GET_ACR_AUDIT_LOG,
        variables: {
          first: args?.first ?? 200,
          after: args?.after ?? null,
          where: args?.where ?? null,
          order: args?.order ?? null,
        },
      }),
      transformResponse: (response: { acrAuditLog: AcrAuditLogConnection | null }) =>
        response.acrAuditLog ?? {
          edges: [],
          pageInfo: { hasNextPage: false, endCursor: null },
          totalCount: 0,
        },
      providesTags: ['AcrAuditLog'],
    }),

    getAcrActionCommands: builder.query<AcrActionCommandsConnection, GetAcrActionCommandsArgs | void>({
      query: (args) => ({
        document: GET_ACR_ACTION_COMMANDS,
        variables: {
          first: args?.first ?? 200,
          after: args?.after ?? null,
          where: args?.where ?? null,
          order: args?.order ?? null,
        },
      }),
      transformResponse: (response: { acrActionCommands: AcrActionCommandsConnection | null }) =>
        response.acrActionCommands ?? {
          edges: [],
          pageInfo: { hasNextPage: false, endCursor: null },
          totalCount: 0,
        },
      providesTags: ['AcrActionCommand'],
    }),

    lockAcrTag: builder.mutation<AcrActionCommandPayload, AcrTagActionInput>({
      query: (input) => ({
        document: LOCK_ACR_TAG,
        variables: { input },
      }),
      transformResponse: (response: { lockAcrTag: AcrActionCommandPayload }) =>
        response.lockAcrTag,
      invalidatesTags: ['AcrActionCommand', 'AcrAuditLog', 'AcrRepository', 'AcrRepositoryTags'],
    }),

    unlockAcrTag: builder.mutation<AcrActionCommandPayload, AcrTagActionInput>({
      query: (input) => ({
        document: UNLOCK_ACR_TAG,
        variables: { input },
      }),
      transformResponse: (response: { unlockAcrTag: AcrActionCommandPayload }) =>
        response.unlockAcrTag,
      invalidatesTags: ['AcrActionCommand', 'AcrAuditLog', 'AcrRepository', 'AcrRepositoryTags'],
    }),

    deleteAcrTag: builder.mutation<AcrActionCommandPayload, AcrTagActionInput>({
      query: (input) => ({
        document: DELETE_ACR_TAG,
        variables: { input },
      }),
      transformResponse: (response: { deleteAcrTag: AcrActionCommandPayload }) =>
        response.deleteAcrTag,
      invalidatesTags: ['AcrActionCommand', 'AcrAuditLog', 'AcrRepository', 'AcrRepositoryTags'],
    }),

    // Live stage vs production image tags for every configured Deploy Dashboard repo. An empty
    // input ({}) returns all repos; the payload's errors array carries a query-level AKS read
    // failure (states is null then) while the request itself still succeeds.
    getRepoDeploymentState: builder.query<RepoDeploymentStatePayload, void>({
      query: () => ({ document: GET_REPO_DEPLOYMENT_STATE, variables: { input: {} } }),
      transformResponse: (response: { repoDeploymentState: RepoDeploymentStatePayload }) =>
        response.repoDeploymentState,
      providesTags: ['RepoDeploymentState'],
    }),

    // Live pod/deployment/rollout health for one whitelisted namespace (worksuite-stage or
    // worksuite-production). The payload's errors array carries a query-level AKS read failure or a
    // namespace whitelist rejection (health is null then) while the request itself still succeeds.
    getAksNamespaceHealth: builder.query<AksNamespaceHealthPayload, string>({
      query: (namespace) => ({
        document: GET_AKS_NAMESPACE_HEALTH,
        variables: { input: { namespace } },
      }),
      transformResponse: (response: { aksNamespaceHealth: AksNamespaceHealthPayload }) =>
        response.aksNamespaceHealth,
      providesTags: (_result, _error, namespace) => [
        { type: 'AksNamespaceHealth', id: namespace },
      ],
    }),

    // Live pending production-stage approvals across the configured Deploy Dashboard repos, read from
    // Azure DevOps. An empty repo filter ({}) returns every repo's pending approvals; the payload's
    // errors array carries a query-level ADO read failure (approvals is null then) while the request
    // itself still succeeds.
    getPendingApprovals: builder.query<PendingApprovalsPayload, { repo?: string } | void>({
      query: (args) => ({
        document: GET_PENDING_APPROVALS,
        variables: { input: { repo: args?.repo ?? null } },
      }),
      transformResponse: (response: { pendingApprovals: PendingApprovalsPayload }) =>
        response.pendingApprovals,
      providesTags: ['PendingApproval'],
    }),

    // Approve a single pending production-stage approval over ADO REST. The multi-select flow calls
    // this once per selected approval; a failed call surfaces in the payload's errors array (success
    // false) rather than throwing, so the caller can aggregate per-repo outcomes.
    approvePipelineRun: builder.mutation<ApprovePipelineRunPayload, ApprovePipelineRunInput>({
      query: (input) => ({
        document: APPROVE_PIPELINE_RUN,
        variables: { input },
      }),
      transformResponse: (response: { approvePipelineRun: ApprovePipelineRunPayload }) =>
        response.approvePipelineRun,
      // Approving consumes the approval, so refetch the pending list once the fan-out settles.
      invalidatesTags: ['PendingApproval'],
    }),

    getFernieConversations: builder.query<FernieConversationPage, GetFernieConversationsArgs>({
      query: (args) => ({ document: GET_FERNIE_CONVERSATIONS, variables: args }),
      transformResponse: (response: { fernieConversations: FernieConversationPage }) =>
        response.fernieConversations,
      providesTags: ['FernieConversation'],
    }),

    getFernieConversation: builder.query<FernieConversationDetail | null, string>({
      query: (sessionId) => ({ document: GET_FERNIE_CONVERSATION, variables: { sessionId } }),
      transformResponse: (response: { fernieConversation: FernieConversationDetail | null }) =>
        response.fernieConversation,
      providesTags: ['FernieConversation'],
    }),
    getFernieThumbsDownQueue: builder.query<FernieThumbsDownQueuePage, GetFernieThumbsDownQueueArgs>({
      query: (args) => ({ document: GET_FERNIE_THUMBS_DOWN_QUEUE, variables: args }),
      transformResponse: (response: { fernieThumbsDownQueue: FernieThumbsDownQueuePage }) =>
        response.fernieThumbsDownQueue,
      providesTags: ['FernieThumbsDownQueue'],
    }),

    setFernieReviewState: builder.mutation<SetFernieReviewStatePayload, SetFernieReviewStateInput>({
      query: (input) => ({ document: SET_FERNIE_REVIEW_STATE, variables: { input } }),
      transformResponse: (response: { setFernieReviewState: SetFernieReviewStatePayload }) =>
        response.setFernieReviewState,
    }),

    fileFernieThemeTask: builder.mutation<FileFernieThemeTaskPayload, FileFernieThemeTaskInput>({
      query: (input) => ({ document: FILE_FERNIE_THEME_TASK, variables: { input } }),
      transformResponse: (response: { fileFernieThemeTask: FileFernieThemeTaskPayload }) =>
        response.fileFernieThemeTask,
      invalidatesTags: ['FernieThumbsDownQueue'],
    }),

    clearFernieRating: builder.mutation<ClearFernieRatingPayload, ClearFernieRatingInput>({
      query: (input) => ({ document: CLEAR_FERNIE_RATING, variables: { input } }),
      transformResponse: (response: { clearFernieRating: ClearFernieRatingPayload }) =>
        response.clearFernieRating,
      invalidatesTags: ['FernieThumbsDownQueue'],
    }),
    getTestLogins: builder.query<TestLoginDocument[], void>({
      query: () => ({ document: GET_TEST_LOGINS }),
      transformResponse: (response: { testLogins: TestLoginDocument[] }) => response.testLogins,
      providesTags: ['TestLogin'],
    }),

    createTestLogin: builder.mutation<TestLoginPayload, { input: CreateTestLoginInput }>({
      query: (variables) => ({ document: CREATE_TEST_LOGIN, variables }),
      transformResponse: (response: { createTestLogin: TestLoginPayload }) => response.createTestLogin,
      invalidatesTags: ['TestLogin'],
    }),

    updateTestLogin: builder.mutation<TestLoginPayload, { input: UpdateTestLoginInput }>({
      query: (variables) => ({ document: UPDATE_TEST_LOGIN, variables }),
      transformResponse: (response: { updateTestLogin: TestLoginPayload }) => response.updateTestLogin,
      invalidatesTags: ['TestLogin'],
    }),

    deleteTestLogin: builder.mutation<DeleteTestLoginPayload, { id: string }>({
      query: (variables) => ({ document: DELETE_TEST_LOGIN, variables }),
      transformResponse: (response: { deleteTestLogin: DeleteTestLoginPayload }) => response.deleteTestLogin,
      invalidatesTags: ['TestLogin'],
    }),
  }),
});

export const {
  useGetResetTenantRequestsQuery,
  useGetResetTenantRequestQuery,
  useCreateResetTenantRequestMutation,
  useApproveResetTenantRequestMutation,
  useRetryTenantRequestMutation,
  useCancelResetTenantRequestMutation,
  useGetDeleteStreamsRequestsQuery,
  useGetDeleteStreamsRequestQuery,
  useGetLatestDeleteStreamsRequestQuery,
  useGetPendingDeleteStreamsRequestsQuery,
  useCreateDeleteStreamsRequestMutation,
  useApproveDeleteStreamsRequestMutation,
  useCancelDeleteStreamsRequestMutation,
  useResetDeleteStreamsRequestMutation,
  useLazyKeyVaultSecretExistsQuery,
  useGetKeyVaultSecretsQuery,
  useLazyGetKeyVaultSecretsQuery,
  useCreateKeyVaultSecretMutation,
  useDeleteKeyVaultSecretMutation,
  useLazyGetDailyTransactionSummariesQuery,
  useGetAvailableTenantsQuery,
  useSwitchTenantMutation,
  useGetEntityCheckpointsQuery,
  useGetCurrentEventStorePositionQuery,
  useUpdateEntityCheckpointMutation,
  useGetFusionAuthApplicationsQuery,
  useGetFusionAuthGroupsQuery,
  useGetFusionAuthGroupQuery,
  useAddFusionAuthGroupMembersMutation,
  useRemoveFusionAuthGroupMembersMutation,
  useAddFusionAuthGroupRolesMutation,
  useRemoveFusionAuthGroupRolesMutation,
  useSearchFusionAuthUsersQuery,
  useLazySearchFusionAuthUsersQuery,
  useGetFusionAuthUserQuery,
  useUpdateFusionAuthUserMutation,
  useReactivateFusionAuthUserMutation,
  useDeleteFusionAuthUserMutation,
  useSetFusionAuthUserTenantMutation,
  useSetFusionAuthUserTenantsMutation,
  useDeleteFusionAuthUsersMutation,
  useGetFusionAuthApplicationQuery,
  useGetFusionAuthApplicationUsersQuery,
  useRegisterApplicationToUsersMutation,
  useUnregisterApplicationFromUsersMutation,
  useAddApplicationRoleToUsersMutation,
  useRemoveApplicationRoleFromUsersMutation,
  useSetFusionAuthUserRegistrationRolesMutation,
  useGetScavengeConfigQuery,
  useUpsertScavengeConfigMutation,
  useLazyGetScavengeLogsQuery,
  useGetEventStreamAggregateConfigQuery,
  useUpsertEventStreamAggregateConfigMutation,
  useLazyGetEventStreamAggregateSnapshotsQuery,
  useGetEventStreamAggregateSnapshotsQuery,
  useSetAdminBackgroundServiceReplicasMutation,
  useGetAdminBackgroundServicePodStatusesQuery,
  useGetAcrRepositoriesQuery,
  useGetAcrRepositoryTagsQuery,
  useGetAcrCleanupSettingsQuery,
  useGetAcrScanTargetsQuery,
  useSetAcrKillSwitchMutation,
  useSetAcrCleanupScheduleMutation,
  useSetAcrRepositoryExcludedMutation,
  useSetAcrRetentionMutation,
  useGetAcrAuditLogQuery,
  useGetAcrActionCommandsQuery,
  useLockAcrTagMutation,
  useUnlockAcrTagMutation,
  useDeleteAcrTagMutation,
  useGetRepoDeploymentStateQuery,
  useGetAksNamespaceHealthQuery,
  useGetPendingApprovalsQuery,
  useApprovePipelineRunMutation,
  useGetFernieConversationsQuery,
  useGetFernieConversationQuery,
  useGetFernieThumbsDownQueueQuery,
  useSetFernieReviewStateMutation,
  useFileFernieThemeTaskMutation,
  useClearFernieRatingMutation,
  useGetTestLoginsQuery,
  useCreateTestLoginMutation,
  useUpdateTestLoginMutation,
  useDeleteTestLoginMutation,
} = adminApi;
